<script lang="ts">
  // @ts-nocheck
  import { faArrowUp, faCopy, faPenToSquare, faComments, faPencil, faTrash, faChevronDown, faChevronRight, faCode, faCheck, faXmark, faUndoAlt, faRotateRight } from "@fortawesome/free-solid-svg-icons";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { Textarea, Button, Toast } from "flowbite-svelte";
  import { Splitpanes, Pane } from "svelte-splitpanes";
  import { useChat } from "@ai-sdk/svelte";
  import { onMount, onDestroy, tick } from "svelte";
  import { marked } from "marked";
  import { fade, fly } from "svelte/transition";
  import { userStore } from "../../../services/stores/user-store.js";
  import { chatManager } from "../../../services/chat/context-manager.js";
  import { get } from "svelte/store";
  import { createEventDispatcher } from "svelte";
  
  const dispatch = createEventDispatcher();
  
  // Subscribe to userStore for reactivity
  $: $userStore;
  import { getEnvironment } from "../../../config/env.js";
  import { type Question, deriveCommonQuestions } from "./common-questions.js";
  import { ensureChatRecord, clearChatMessages, appendUserMessage, appendAssistantMessage, refreshChatStore, replaceMessagesForChat } from "../../../services/stores/chat-store.js";
  import { saveUserMetadata, getDocumentsByCustomer, getProjectsByCustomer, getMyCustomers } from "../../../services/api/data-handler.js";
  import { api } from "../../../services/api/api-client.js";
  import { selectedObjects } from "../../../services/stores/selected-objects-store.js";
  import { newChatSignal } from "../../../services/stores/new-chat-signal-store.js";
  import { chatSessionStore } from "../../../services/stores/chat-session-store.js";
  import type { Document } from "../../../services/api/types.js";
  import type { Project } from "../../../services/api/types.js";
  import { v4 as uuidv4, v5 as uuidv5 } from "uuid";
  import { logInfo, logError, logWarning } from "../../../services/logging/log-handler.js";

  // Module-level persistent storage for manually loaded session IDs
  // This persists across component remounts
  const manuallyLoadedSessionIds = new Set<string>();
  
  // Track which chat_id each manually loaded session maps to
  // This allows us to check if a context switch would conflict with a loaded session
  const manuallyLoadedSessionChatIds = new Map<string, string>(); // sessionId -> chatId
  
  // Persist the mapping to localStorage to survive component remounts
  const SESSION_CHAT_MAPPING_KEY = "chatbot_session_chat_mapping";
  
  function loadSessionChatMapping(): void {
    try {
      const stored = localStorage.getItem(SESSION_CHAT_MAPPING_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        manuallyLoadedSessionChatIds.clear();
        for (const [sessionId, chatId] of Object.entries(parsed)) {
          if (typeof sessionId === "string" && typeof chatId === "string") {
            manuallyLoadedSessionChatIds.set(sessionId, chatId);
          }
        }
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error loading session->chat mapping" });
    }
  }
  
  function saveSessionChatMapping(): void {
    try {
      const toStore: Record<string, string> = {};
      for (const [sessionId, chatId] of manuallyLoadedSessionChatIds.entries()) {
        toStore[sessionId] = chatId;
      }
      localStorage.setItem(SESSION_CHAT_MAPPING_KEY, JSON.stringify(toStore));
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error saving session->chat mapping" });
    }
  }
  
  // Load mapping immediately when module loads (before any component instances)
  if (typeof window !== "undefined") {
    loadSessionChatMapping();
  }
  
  // Persist currentChatSessionId across component remounts using localStorage
  // This ensures the session ID is preserved even when the component is destroyed and recreated
  const PERSISTED_SESSION_ID_KEY = "chatbot_current_session_id";
  
  function getPersistedSessionId(): string | null {
    try {
      return localStorage.getItem(PERSISTED_SESSION_ID_KEY);
    } catch {
      return null;
    }
  }
  
  function setPersistedSessionId(sessionId: string | null): void {
    try {
      if (sessionId) {
        localStorage.setItem(PERSISTED_SESSION_ID_KEY, sessionId);
      } else {
        localStorage.removeItem(PERSISTED_SESSION_ID_KEY);
      }
    } catch {
      // Ignore localStorage errors
    }
  }
  
  // New chat counter for session-scoped numbering
  // Store both counter and session_id to reset on new login
  const NEW_CHAT_COUNTER_KEY = "chatbot_new_chat_counter";
  const NEW_CHAT_SESSION_ID_KEY = "chatbot_new_chat_session_id";
  
  function ensureSessionTracking(): void {
    try {
      const store = get(userStore);
      const currentSessionId = store.session_id;
      
      if (!currentSessionId) {
        sessionStorage.removeItem(NEW_CHAT_COUNTER_KEY);
        sessionStorage.removeItem(NEW_CHAT_SESSION_ID_KEY);
        return;
      }
      
      const storedSessionId = sessionStorage.getItem(NEW_CHAT_SESSION_ID_KEY);
      if (storedSessionId !== currentSessionId) {
        sessionStorage.setItem(NEW_CHAT_COUNTER_KEY, "0");
        sessionStorage.setItem(NEW_CHAT_SESSION_ID_KEY, currentSessionId);
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Ensure session tracking error" });
    }
  }
  
  function getNewChatCounter(): number {
    try {
      // First ensure session tracking is correct (this will reset if session changed)
      ensureSessionTracking();
      
      const stored = sessionStorage.getItem(NEW_CHAT_COUNTER_KEY);
      const value = stored ? parseInt(stored, 10) : 0;
      return value;
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "get new chat counter() error" });
      return 0;
    }
  }
  
  function incrementNewChatCounter(): number {
    try {
      // First ensure session tracking is correct (this will reset if session changed)
      ensureSessionTracking();
      
      // Now get the current value and increment it
      const stored = sessionStorage.getItem(NEW_CHAT_COUNTER_KEY);
      const current = stored ? parseInt(stored, 10) : 0;
      const next = current + 1;
      sessionStorage.setItem(NEW_CHAT_COUNTER_KEY, next.toString());
      return next;
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Increment new chat counter error" });
      return 1;
    }
  }
  
  function getNewChatName(): string {
    const counter = incrementNewChatCounter();
    const name = `New Chat ${counter}`;
    return name;
  }
  
  function getDefaultNewChatName(): string {
    // Get name without incrementing - used for fallbacks when loading existing sessions
    const current = getNewChatCounter();
    const counter = current + 1;
    const name = `New Chat ${counter}`;
    return name;
  }
  
  // Session names are now handled by the reactive statement - no need for manual mapping

  const config = getEnvironment();
  const none = "";

  export let external: boolean = false;
  export let customer_code: string = none;
  export let projects: any[] = [];
  export let documents: any[] = [];
  export let stage: string = none;
  export let sessionToLoad: string | null = null;
  export let customer_id: string | null = null;

  // Streaming speed multiplier (1 = normal speed, 4 = 4x speed)
  const STREAMING_SPEED_MULTIPLIER = 8;

  // Format stage name for display (convert snake_case to title case)
  function formatStageName(stageValue: string): string {
    if (!stageValue || stageValue === none) return "";
    
    // Map stage keys to display names
    const stageMap: Record<string, string> = {
      "protocol": "Protocol",
      "screening": "Screening",
      "data_extraction": "Data extraction",
      "report": "Report",
      "included_publications": "Included publications",
    };
    
    return stageMap[stageValue] || stageValue
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Compute dynamic placeholder text based on context
  // IMPORTANT: Check projects first (matching selectionType logic: projects.length > 0 ? "projects" : "documents")
  // This ensures project context is shown even when documents are also present (projects auto-select their documents)
  $: placeholderText = (() => {
    // Use derivedDocuments and derivedProjects from selectedObjects for accurate context
    const docCount = derivedDocuments.length;
    const projectCount = derivedProjects.length;
    
    // Check projects first - if projects are selected, show project context (even if documents are also present)
    if (projectCount > 0) {
      // Project context - include stage if available
      const formattedStage = formatStageName(stage);
      if (formattedStage) {
        return `Ask a question for the ${formattedStage.toLowerCase()} stage...`;
      } else {
        return "Ask a question for this project...";
      }
    } else if (docCount > 0) {
      // Document context (only when no projects are selected)
      if (docCount === 1) {
        return "Ask a question about this document...";
      } else {
        return "Ask a question about these documents...";
      }
    } else {
      // Default fallback
      return "Ask a question...";
    }
  })();

  // Helper functions to safely access document properties without type assertions
  function getDocPathFromDocument(doc: Document): string {
    return (doc as any).doc_path || `${doc.doc_key}.pdf`;
  }

  function getDocFilenameFromDocument(doc: Document): string {
    return (doc as any).doc_filename || doc.file_name || doc.doc_title || getDocPathFromDocument(doc);
  }

  /**
   * Derives file_name from doc_key and doc_name by extracting extension from doc_name
   * @param doc_key - Unique document identifier
   * @param doc_name - User-friendly document name with extension (can be in format "Title [filename.ext]")
   * @returns file_name in format doc_key + extension
   * @throws Error if extension cannot be determined from doc_name
   */
  function deriveFileName(doc_key: string, doc_name: string): string {
    if (!doc_key || !doc_name) {
      throw new Error(`Cannot derive file_name: doc_key="${doc_key}", doc_name="${doc_name}"`);
    }

    // Check for bracketed filename format: "Title [filename.ext]"
    // This format is used for PDFs where the attachment_title is shown with the filename
    const bracketMatch = doc_name.match(/\[([^\]]+\.[a-zA-Z0-9]+)\]$/);
    if (bracketMatch) {
      const filenameInBrackets = bracketMatch[1];
      const lastDotIndex = filenameInBrackets.lastIndexOf('.');
      if (lastDotIndex !== -1 && lastDotIndex !== 0 && lastDotIndex !== filenameInBrackets.length - 1) {
        const extension = filenameInBrackets.substring(lastDotIndex);
        return `${doc_key}${extension}`;
      }
    }

    // Fall back to extracting extension from the end of doc_name
    const lastDotIndex = doc_name.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0 || lastDotIndex === doc_name.length - 1) {
      throw new Error(`Cannot determine extension from doc_name: "${doc_name}". doc_name must have a valid file extension.`);
    }

    const extension = doc_name.substring(lastDotIndex);
    return `${doc_key}${extension}`;
  }

  /**
   * Extracts doc_name from document object - throws error if not available
   * @param doc - Document object
   * @returns doc_name (user-friendly name)
   * @throws Error if doc_name cannot be determined
   */
  function extractDocName(doc: any): string {
    // Use doc_name field - NO FALLBACKS
    if (!doc.doc_name || typeof doc.doc_name !== "string" || doc.doc_name.trim() === "") {
      throw new Error(`Cannot extract doc_name from document. Missing required field 'doc_name'. Document: ${JSON.stringify(doc)}`);
    }
    return doc.doc_name.trim();
  }

  // Session management
  // Initialize from persisted value if available, otherwise null
  let currentChatSessionId: string | null = getPersistedSessionId();
  // Don't increment counter on initialization - only increment when user explicitly creates a new chat
  // But we need to ensure session tracking is set up
  ensureSessionTracking();
  let currentSessionName: string = getDefaultNewChatName(); // Initialize without incrementing
  let currentSessionStarted: string | null = null;
  
  let isEditingSessionName: boolean = false;
  let originalSessionName: string = ""; // Store original name when editing starts
  let sessionNameInput: HTMLInputElement | null = null;
  let sessions: any[] = [];
  let isLoadingSessions: boolean = false;
  
  // Associated chats state
  type TimePeriod = "Today" | "Yesterday" | "This Week" | "Last Week" | "Last Month" | "Earlier";
  interface AssociatedChatSession {
    chat_session_id: string;
    name: string;
    started: string;
    last_message_time?: string;
    last_context_key?: string;
  }
  let associatedChats: AssociatedChatSession[] = [];
  let isLoadingAssociatedChats = false;
  let currentChatContextKey: string | null = null;
  
  // Expand/collapse state for associated chats time periods
  let associatedChatsTimePeriodExpanded: Record<TimePeriod, boolean> = {
    "Today": true,
    "Yesterday": true,
    "This Week": true,
    "Last Week": true,
    "Last Month": true,
    "Earlier": true,
  };
  
  // Toast state - track which message index should show the toast
  let showCopyToast: number | null = null;
  let copyToastButtonRef: HTMLElement | null = null;
  
  // Toast state for question copy - track which message index should show the toast
  let showQuestionCopyToast: number | null = null;
  let questionCopyToastButtonRef: HTMLElement | null = null;
  
  function createCopyHandler(index: number) {
    return (event: MouseEvent) => {
      copyToastButtonRef = event.currentTarget as HTMLElement;
      copyToClipboard(index);
    };
  }
  
  function createQuestionCopyHandler(index: number) {
    return (event: MouseEvent) => {
      questionCopyToastButtonRef = event.currentTarget as HTMLElement;
      copyQuestionToClipboard(index);
    };
  }
  
  // Track next step choices for each message (qaId -> choice)
  type NextStepChoice = "resubmit_for_report_stage" | "resubmit_for_publications_stage" | "resubmit_for_protocol_stage" | "do_nothing" | null;
  const nextStepChoices = new Map<string, NextStepChoice>();
  
  // Constants for next_step values
  const NEXT_STEP_NONE = "none";
  const NEXT_STEP_REPORT_STAGE_RESUBMIT = "report_stage_resubmit";
  const NEXT_STEP_STAGE_RESUBMIT = "stage_resubmit";
  
  // Get next step message text based on next_step value
  function getNextStepMessage(nextStepValue: string): string {
    switch (nextStepValue) {
      case NEXT_STEP_REPORT_STAGE_RESUBMIT:
        return "What do you want to do?";
      case NEXT_STEP_STAGE_RESUBMIT:
        return "What do you want to do?";
      default:
        return "";
    }
  }
  
  // Get next step options (buttons) based on next_step value
  function getNextStepOptions(nextStepValue: string): Array<{ label: string; value: NextStepChoice; isResubmit: boolean }> | null {
    switch (nextStepValue) {
      case NEXT_STEP_REPORT_STAGE_RESUBMIT:
        return [
          { label: "Resubmit for report stage", value: "resubmit_for_report_stage", isResubmit: true },
          { label: "Do nothing", value: "do_nothing", isResubmit: false }
        ];
      case NEXT_STEP_STAGE_RESUBMIT:
        return [
          { label: "Resubmit for report stage", value: "resubmit_for_report_stage", isResubmit: true },
          { label: "Resubmit for included publications stage", value: "resubmit_for_publications_stage", isResubmit: true },
          { label: "Resubmit for protocol stage", value: "resubmit_for_protocol_stage", isResubmit: true },
          { label: "Do nothing", value: "do_nothing", isResubmit: false }
        ];
      default:
        return null;
    }
  }
  
  // Map choice value to stage name
  function getStageFromChoice(choice: NextStepChoice): string | null {
    switch (choice) {
      case "resubmit_for_report_stage":
        return "report";
      case "resubmit_for_publications_stage":
        return "included_publications";
      case "resubmit_for_protocol_stage":
        return "protocol";
      default:
        return null;
    }
  }
  
  // Handle next step choice
  async function handleNextStepChoice(qaId: string, nextStepValue: string, choice: NextStepChoice) {
    // Store the choice in memory
    nextStepChoices.set(qaId, choice);
    
    // Save the choice to the route JSON in messageMetadata
    const metadata = messageMetadata.get(qaId);
    if (metadata) {
      const updatedRoute = {
        ...metadata.route,
        next_step_choice: choice
      };
      messageMetadata.set(qaId, {
        ...metadata,
        route: updatedRoute
      });
      saveMessageMetadata();
      
      // Trigger UI update to show the choice immediately
      const sessionId = currentChatSessionId || currentChatId;
      if (sessionId) {
        await tick();
        syncChats(sessionId);
      }
    }
    
    // If "do nothing", just exit
    if (choice === "do_nothing") {
      return;
    }
    
    // Get the stage from the choice
    const stageToUse = getStageFromChoice(choice);
    if (!stageToUse) {
      return;
    }
    
    // 1. Set stage by dispatching event to parent
    dispatch("stageChange", { stage: stageToUse });
    
    // 2. Find the original question text (user message with same chat_qa_id)
    const allMessages = $messages || [];
    const userMessage = allMessages.find((msg: any) => 
      msg.chat_qa_id === qaId && msg.role === "user"
    );
    
    if (userMessage && userMessage.content) {
      // 3. Set question as input
      $input = userMessage.content;
      
      // Wait for stage prop to update (dispatch event + reactive prop update)
      await tick();
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 4. Submit the question (stage prop should now be updated)
      const submitEvent = new Event("submit");
      await handleSubmitWithLogging(submitEvent);
    }
  }
  
  // Get the choice text to display after user makes a choice
  function getNextStepChoiceText(choice: NextStepChoice): string {
    if (!choice) {
      return "";
    }
    
    if (choice === "do_nothing") {
      return "User did not want to resubmit";
    }
    
    const stageToUse = getStageFromChoice(choice);
    if (stageToUse) {
      const stageName = formatStageName(stageToUse);
      return `User asked to resubmit the question with the ${stageName} stage.`;
    }
    
    return "";
  }
  
  // Store current messages for copy function
  let currentMessagesForCopy: any[] = [];
  $: currentMessagesForCopy = $messages || [];
  
  // Subscribe to session name changes from ChatHistory
  $: $chatSessionStore;
  $: {
    const update = $chatSessionStore;
    if (update && update.type === "nameChanged" && update.sessionId && update.newName) {
      // Update current session name if this is the current chat
      if (update.sessionId === currentChatSessionId && !isEditingSessionName) {
        currentSessionName = update.newName;
        // Update in sessions list if it exists
        const sessionIndex = sessions.findIndex(s => s.chat_session_id === currentChatSessionId);
        if (sessionIndex !== -1) {
          sessions[sessionIndex].name = update.newName;
          sessions = [...sessions]; // Trigger reactivity
        }
      }
      
      // Update associated chats if the renamed session is in the list
      const associatedChatIndex = associatedChats.findIndex(chat => chat.chat_session_id === update.sessionId);
      if (associatedChatIndex !== -1) {
        associatedChats[associatedChatIndex].name = update.newName;
        associatedChats = [...associatedChats]; // Trigger reactivity
      }
    }
  }

  // CRITICAL: Reactive statement to keep session name in sync with currentChatSessionId
  // This ensures that whenever currentChatSessionId is set, the session name is automatically updated
  // from the sessions list, even if the component remounts or maps are cleared
  // This is the SINGLE SOURCE OF TRUTH for session name coordination
  // NOTE: We do NOT set currentChatSessionId here to avoid cyclical dependencies
  // The restoration of currentChatSessionId from currentChatId is handled in onMount
  // We use a separate reactive statement that only depends on currentChatSessionId and sessions
  // to avoid any indirect dependencies on currentChatId
  // IMPORTANT: Don't update if user is currently editing the name - this prevents the reactive
  // statement from overwriting user input while they're typing
  $: if (currentChatSessionId && sessions.length > 0 && !isLoadingSession && !isEditingSessionName) {
    const session = sessions.find(s => s.chat_session_id === currentChatSessionId);
    if (session) {
      const expectedName = session.name || getDefaultNewChatName();
      // Only update if the name doesn't match what it should be
      // This ensures the name is always correct, even after remounts
      if (currentSessionName !== expectedName) {
        currentSessionName = expectedName;
        // Use last_sent if available, otherwise fall back to started
        currentSessionStarted = session.last_sent || session.started;
      }
    }
  }
  
  // Helper function to determine if we're in project mode (same logic as ViewResults.svelte)
  // Projects mode is only active when ALL projects with selected documents are fully selected
  function isProjectMode(selected: { projects: any[]; documents: any[] }): boolean {
    if (selected.documents.length === 0) {
      // No documents selected - use projects if available
      return selected.projects.length > 0;
    }
    
    // Get all unique project IDs from selected documents
    const projectsWithSelectedDocs = new Set(selected.documents.map((doc: any) => doc.project_id));
    
    // Get all project IDs that are fully selected
    const fullySelectedProjectIds = new Set(selected.projects.map((p: any) => p.project_id));
    
    // Show projects mode only if all projects with selected documents are fully selected
    return projectsWithSelectedDocs.size > 0 && 
           Array.from(projectsWithSelectedDocs).every((projectId) => fullySelectedProjectIds.has(projectId));
  }

  // Derive current context from selectedObjects store (reactive to selection changes)
  let derivedProjects: any[] = [];
  let derivedDocuments: any[] = [];
  let effectiveProjectMode: boolean = false;
  
  $: {
    // Subscribe to selectedObjects to make it reactive
    const selected = $selectedObjects;
    
    // Determine if we're in project mode using the same logic as ViewResults.svelte
    effectiveProjectMode = isProjectMode(selected);
    
    // Convert selectedObjects to the format expected by the rest of the component
    // Only include projects if we're actually in project mode
    derivedProjects = effectiveProjectMode ? (selected.projects || []) : [];
    derivedDocuments = (selected.documents || []).map((selectedDoc: any, index: number) => {
      // Validate doc_key - throw error if missing
      if (!selectedDoc.doc_key || typeof selectedDoc.doc_key !== "string" || selectedDoc.doc_key.trim() === "") {
        throw new Error(`Document at index ${index} in selectedObjects is missing required field 'doc_key'. Document: ${JSON.stringify(selectedDoc)}`);
      }
      
      // Validate file_name - throw error if missing
      if (!selectedDoc.file_name || typeof selectedDoc.file_name !== "string" || selectedDoc.file_name.trim() === "") {
        throw new Error(`Document at index ${index} in selectedObjects is missing required field 'file_name'. Document: ${JSON.stringify(selectedDoc)}`);
      }
      
      // Validate doc_name - throw error if missing
      if (!selectedDoc.doc_name || typeof selectedDoc.doc_name !== "string" || selectedDoc.doc_name.trim() === "") {
        throw new Error(`Document at index ${index} in selectedObjects is missing required field 'doc_name'. Document: ${JSON.stringify(selectedDoc)}`);
      }
      
      // Validate project_id - throw error if missing
      if (!selectedDoc.project_id || typeof selectedDoc.project_id !== "string" || selectedDoc.project_id.trim() === "") {
        throw new Error(`Document at index ${index} in selectedObjects is missing required field 'project_id'. Document: ${JSON.stringify(selectedDoc)}`);
      }
      
      return {
        doc_key: selectedDoc.doc_key.trim(),
        file_name: selectedDoc.file_name.trim(),
        doc_name: selectedDoc.doc_name.trim(),
        project_id: selectedDoc.project_id.trim(),
        stage: selectedDoc.stage, // Document stage (Protocol, Screening, etc.)
      };
    }).filter((d: any) => d && d.doc_key && d.doc_key.length > 0);
    
  }
  
  // Reactive common questions based on current context from selectedObjects
  let currentQuestions: Question[] = [];
  $: currentQuestions = deriveCommonQuestions(derivedProjects, derivedDocuments);

  // Splitter position for chat and FAQ/History panels
  let splitPosition: number = 75;
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let hasLoadedPosition = false;
  let lastSavedPosition: number | null = null; // Track last saved to avoid duplicate saves
  let pendingSave = false; // Track if there's a pending save

  // Load splitter position from user metadata when user store is ready
  $: {
    if ($userStore.user && !hasLoadedPosition) {
      if ($userStore.user.metadata?.chat_sidebar_splitter) {
        const saved = $userStore.user.metadata.chat_sidebar_splitter;
        if (typeof saved === "number" && saved > 20 && saved < 90) {
          splitPosition = saved;
          lastSavedPosition = saved;
          hasLoadedPosition = true;
        } else {
          hasLoadedPosition = true;
        }
      } else {
        hasLoadedPosition = true;
      }
    }
  }

  // Actually perform the save
  async function doSaveSplitter() {
    // Skip if position hasn't changed from last save
    if (lastSavedPosition !== null && Math.abs(splitPosition - lastSavedPosition) < 0.5) {
      pendingSave = false;
      return;
    }
    
    try {
      const store = get(userStore);
      if (!store.user) return;
      const newMetadata = {
        ...store.user.metadata,
        chat_sidebar_splitter: splitPosition,
      };
      await saveUserMetadata(newMetadata);
      lastSavedPosition = splitPosition;
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error saving splitter position" });
    } finally {
      pendingSave = false;
    }
  }

  // Save splitter position to user metadata (debounced)
  function saveSplitterPosition() {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    pendingSave = true;
    saveTimeout = setTimeout(() => {
      doSaveSplitter();
      saveTimeout = null;
    }, 1000); // Debounce by 1000ms (increased from 500ms)
  }

  // Handle splitter resize
  function handleSplitterResized(event: CustomEvent) {
    const panes = event.detail as Array<{ size: number; min?: number; max?: number }>;
    if (panes && Array.isArray(panes) && panes.length > 0 && panes[0]?.size !== undefined) {
      splitPosition = panes[0].size;
      saveSplitterPosition();
    }
  }

  let messagesContainer: HTMLUListElement | null = null;
  let loadingText = "Thinking...";
  let revealedText = "";

  // Track current chat identity for persistence
  let currentChatId: string = none;
  let currentSelectionType: "projects" | "documents" = "projects";
  let currentIds: string[] = [];
  let lastAppliedChatId: string = none;
  let currentRequestId: string | null = null;
  
  // Track the context (chat_id) that was used for the last question in the current session
  // This helps us detect when context changes within a session
  let lastQuestionContextId: string | null = null;
  
  // Track context for each chat_id to display in the UI
  // Format: { chat_context_key: { type: 'documents' | 'projects', items: string[] } }
  // Uses chat_context_key (database ID) instead of generated chat_id
  let chatContexts = new Map<string, { type: 'documents' | 'projects', items: string[] }>();
  
  // Persist chatContexts to localStorage so they survive remounts
  const CHAT_CONTEXTS_STORAGE_KEY = 'chatContexts';
  const QA_INDEX_TO_CHAT_ID_KEY = 'qaIndexToChatId';
  
  // Map session chat_id to Q&A index to chat_id mapping
  let qaIndexMappings = new Map<string, Map<number, string>>();
  
  function loadChatContexts() {
    try {
      const stored = localStorage.getItem(CHAT_CONTEXTS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        chatContexts = new Map(Object.entries(parsed).map(([key, value]: [string, any]) => [key, value]));
      }
      
      // Also load Q&A index mappings
      const mappingsStored = localStorage.getItem(QA_INDEX_TO_CHAT_ID_KEY);
      if (mappingsStored) {
        const parsed = JSON.parse(mappingsStored);
        qaIndexMappings = new Map(Object.entries(parsed).map(([sessionChatId, mapping]: [string, any]) => [
          sessionChatId,
          new Map(Object.entries(mapping).map(([index, chatId]: [string, any]) => [Number(index), chatId]))
        ]));
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error loading chatContexts" });
    }
  }
  
  function saveChatContexts() {
    try {
      const toStore: Record<string, any> = {};
      for (const [key, value] of chatContexts.entries()) {
        toStore[key] = value;
      }
      localStorage.setItem(CHAT_CONTEXTS_STORAGE_KEY, JSON.stringify(toStore));
      
      // Also save Q&A index mappings
      const mappingsToStore: Record<string, Record<string, string>> = {};
      for (const [sessionChatId, mapping] of qaIndexMappings.entries()) {
        mappingsToStore[sessionChatId] = Object.fromEntries(mapping);
      }
      localStorage.setItem(QA_INDEX_TO_CHAT_ID_KEY, JSON.stringify(mappingsToStore));
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error saving chatContexts" });
    }
  }
  
  // Track which contexts are expanded
  let contextExpanded = new Set<string>();
  
  // Helper function to get chat_context_key from a message (handles type safety)
  // This is the database ID that groups messages by context, not a generated chat_id
  function getMessageContextKey(message: any): string | undefined {
    return message?.chat_context_key;
  }
  
  // Track request timestamps for duration calculation
  let requestStartTimes = new Map<string, number>();
  let messageMetadata = new Map<string, { timestamp: Date; duration?: number; route?: any }>();
  // Track qaId to requestId mapping so we can clear thinking state when streaming starts
  const qaIdToRequestId = new Map<string, string>();
  
  // Toggle for showing route JSON
  let showRoutes = false;
  
  // Track chat_context_key and chat_qa_id from backend responses
  // These are the database IDs that should be used for grouping messages
  // Note: chat_context_key is deterministic (composite key from document IDs), so we can calculate it on frontend
  const requestIdToContextKey = new Map<string, string>();
  const requestIdToQaId = new Map<string, string>();
  const requestIdToQuestion = new Map<string, string>(); // Store question text for new sessions
  const requestIdToAnswerAdded = new Map<string, boolean>(); // Track if answer was already added (to prevent duplicates)
  const requestIdToTempSessionId = new Map<string, string>(); // Track temporary session IDs for new sessions
  
  // Streaming state for assistant messages (teleprompter effect)
  const streamingMessages = new Map<string, string>(); // qaId -> displayed text
  const streamingComplete = new Set<string>(); // qaId -> whether streaming is complete
  const streamingIntervals = new Map<string, ReturnType<typeof setInterval>>(); // qaId -> interval ID
  const messagesAlreadyShown = new Set<string>(); // qaId -> whether message was already displayed (for history/redisplay)
  const newlyAddedAnswers = new Set<string>(); // qaId -> messages just added via appendAssistantMessage (should stream)
  

  const METADATA_STORAGE_KEY = 'chatMessageMetadata';
  
  function loadMessageMetadata() {
    try {
      const stored = localStorage.getItem(METADATA_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        let loadedCount = 0;
        for (const [key, value] of Object.entries(parsed)) {
          if (value && typeof value === 'object' && value !== null && 'timestamp' in value && 'duration' in value) {
            const val = value as { timestamp: string; duration: number; route?: any };
            const timestamp = new Date(val.timestamp);
            messageMetadata.set(key, {
              timestamp: timestamp,
              duration: val.duration,
              route: val.route || undefined,
            });
            
            // Restore next_step_choice from route if it exists
            if (val.route && val.route.next_step_choice) {
              nextStepChoices.set(key, val.route.next_step_choice);
            }
            
            loadedCount++;
          }
        }
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error loading message metadata" });
    }
  }
  
  function saveMessageMetadata() {
    try {
      const toStore: Record<string, any> = {};
      for (const [key, value] of messageMetadata.entries()) {
        toStore[key] = {
          timestamp: value.timestamp.toISOString(),
          duration: value.duration,
          route: value.route,
        };
      }
      localStorage.setItem(METADATA_STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error saving message metadata" });
    }
  }

  // Use the unified chat manager for reactive state
  const chatState = chatManager.state;

  // Get user store for object_id and session_id
  const store = get(userStore);
  const object_id = store.user?.oid;
  const session_id = store.session_id;

  // Chat API - using chat2Ask endpoint
  const apiUrl = `${config.serverUrl}/chat2Ask`;
  const { messages, input, handleSubmit, reload, setMessages } = useChat({
    api: apiUrl,
    streamProtocol: "text",
    onResponse: async (response) => {
      // Check response status first - handle errors before trying to parse
      if (!response.ok) {
        // Clone response before reading (useChat might consume it)
        const responseClone = response.clone();
        const errorText = await responseClone.text();
        logError(new Error(errorText), "Chatbot2", undefined, { message: `API error ${response.status}` });
        
        // Try to extract error message from response
        let errorMessage = `Server error (${response.status})`;
        try {
          // Only try to parse if it looks like JSON
          if (errorText.trim().startsWith('{') || errorText.trim().startsWith('[')) {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorJson.message || errorMessage;
          } else {
            // Plain text error - use as-is (but limit length)
            if (errorText && errorText.length < 500) {
              errorMessage = errorText;
            } else if (errorText) {
              errorMessage = errorText.substring(0, 500) + '...';
            }
          }
        } catch (e) {
          // If not JSON, use error text directly (but limit length)
          if (errorText && errorText.length < 500) {
            errorMessage = errorText;
          } else if (errorText) {
            errorMessage = errorText.substring(0, 500) + '...';
          }
        }
        
        // Get session and qaId for error message
        const sessionId = currentChatSessionId || currentChatId;
        const qaId = requestIdToQaId.get(currentRequestId);
        const chat_context_key = requestIdToContextKey.get(currentRequestId);
        
        if (sessionId && qaId && currentRequestId) {
          // Ensure context is stored in chatContexts for UI display (if we have chat_context_key)
          if (chat_context_key && !chatContexts.has(chat_context_key)) {
            // Reconstruct context items from current selection
            const contextItems: string[] = [];
            if (currentSelectionType === "projects") {
              const selected = get(selectedObjects);
              const projectNames = (selected.projects || [])
                .map((p: any) => p.project_name || p.project_id)
                .filter((name: string) => name);
              contextItems.push(...projectNames);
            } else {
              const selected = get(selectedObjects);
              const documentNames: string[] = [];
              (selected.documents || []).forEach((d: any) => {
                const name = d.doc_name || "";
                if (name) documentNames.push(name);
              });
              contextItems.push(...documentNames);
            }
            
            if (contextItems.length > 0) {
              chatContexts.set(chat_context_key, { 
                type: currentSelectionType || "documents", 
                items: contextItems 
              });
              saveChatContexts();
            }
          }
          
          // Store error as assistant message (plain text, not JSON)
          chatManager.appendAssistantMessage(sessionId, errorMessage, qaId);
          newlyAddedAnswers.add(qaId);
          
          // Complete chat to clear thinking state
          chatManager.completeChat(sessionId, currentRequestId);
          
          // Sync messages to display error (error message will inherit chat_context_key from user question)
          syncChats(sessionId);
        } else {
          logError(new Error("Failed to display API error: No session, qaId, or requestId available."), "Chatbot2", undefined, {
            sessionId,
            qaId,
            currentRequestId,
            chat_context_key
          });
        }
        
        return; // Don't process further
      }
      
      // Extract chat_context_key and chat_qa_id from response metadata
      // The backend now returns JSON with {answer, chat_context_key, chat_qa_id, chat_session_id}
      // Note: chat_context_key is deterministic and should match what we calculated on frontend
      try {
        // Check if response is JSON (backend returns JSON, not streaming)
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          // Clone the response so useChat can still read it for streaming (if needed)
          const responseClone = response.clone();
          const responseData = await responseClone.json();
          
          if (responseData.chat_context_key && currentRequestId) {
            // Store chat_context_key for this request so we can use it in onFinish
            requestIdToContextKey.set(currentRequestId, responseData.chat_context_key);
            if (responseData.chat_qa_id) {
              requestIdToQaId.set(currentRequestId, responseData.chat_qa_id);
            }
            
            // Ensure context is stored in chatContexts for UI display
            // The context should already be stored in handleSubmitWithLogging, but ensure it's there
            if (!chatContexts.has(responseData.chat_context_key)) {
              // Reconstruct context items from current selection
              const contextItems: string[] = [];

              // projects selection type
              if (currentSelectionType === "projects") { 
                const selected = get(selectedObjects);
                const projectNames = (selected.projects || [])
                  .map((p: any) => p.project_name || p.project_id)
                  .filter((name: string) => name);
                contextItems.push(...projectNames);

              // Documents selection type
              } else { 
                const selected = get(selectedObjects);
                const documentNames: string[] = [];
                (selected.documents || []).forEach((d: any) => {
                  const name = d.doc_name || "";
                  if (name) documentNames.push(name);
                });
                contextItems.push(...documentNames);
              }
              
              if (contextItems.length > 0) {
                chatContexts.set(responseData.chat_context_key, { 
                  type: currentSelectionType || "documents", 
                  items: contextItems 
                });
                saveChatContexts();
              }
            }
          }
          
          // If server returned a new chat_session_id (when we didn't send one), store Q&A pairs now
          // Server creates the session with a UUID - no temporary IDs or keys
          if (responseData.chat_session_id && currentRequestId) {
            const isNewSession = !currentChatSessionId;
            const sessionChanged = currentChatSessionId && currentChatSessionId !== responseData.chat_session_id;
            
            // CRITICAL: If session ID changed unexpectedly (not a new session), we need to migrate messages
            if (sessionChanged && !isNewSession) {
              logWarning(`Session ID changed unexpectedly from ${currentChatSessionId} to ${responseData.chat_session_id}. Migrating messages...`, "Chatbot2", undefined);
              
              // Get all messages from old session
              const oldMessages = chatManager.getMessagesForChat(currentChatSessionId);
              
              // Migrate messages to new session
              for (const msg of oldMessages) {
                if (msg.role === 'user') {
                  appendUserMessage(responseData.chat_session_id, msg.content, { 
                    customer: customer_code, 
                    selectionType: currentSelectionType, 
                    ids: currentIds 
                  }, msg.chat_qa_id);
                } else if (msg.role === 'assistant') {
                  chatManager.appendAssistantMessage(responseData.chat_session_id, msg.content, msg.chat_qa_id);
                }
              }
              
              // Migrate context mappings
              const oldMapping = qaIndexMappings.get(currentChatSessionId);
              if (oldMapping) {
                if (!qaIndexMappings.has(responseData.chat_session_id)) {
                  qaIndexMappings.set(responseData.chat_session_id, new Map());
                }
                const newMapping = qaIndexMappings.get(responseData.chat_session_id)!;
                for (const [index, contextKey] of oldMapping) {
                  newMapping.set(index, contextKey);
                }
                saveChatContexts();
              }
            }
            
            if (isNewSession || sessionChanged) {
              currentChatSessionId = responseData.chat_session_id;
              currentChatId = responseData.chat_session_id;
              setPersistedSessionId(responseData.chat_session_id);
              
              // For new sessions, migrate from temporary session to real session
              if (isNewSession) {
                const tempSessionId = requestIdToTempSessionId.get(currentRequestId);
                const qaId = requestIdToQaId.get(currentRequestId);
                const chat_context_key = responseData.chat_context_key || requestIdToContextKey.get(currentRequestId);
                
                if (qaId && chat_context_key) {
                  // Ensure the chat record exists
                  ensureChatRecord(responseData.chat_session_id, { 
                    customer: customer_code, 
                    selectionType: currentSelectionType, 
                    ids: currentIds 
                  });
                  
                  // Save the session name if it's been set (e.g., "New Chat 1", "New Chat 2", etc.)
                  if (currentSessionName && currentSessionName.trim() && object_id) {
                    try {
                      await api.post("/chat2RenameSession", {
                        object_id: object_id,
                        chat_session_id: responseData.chat_session_id,
                        new_name: currentSessionName.trim(),
                      });
                    } catch (error) {
                      logError(error, "Chatbot2", undefined, { message: "Error setting initial session name" });
                      // Don't throw - this is best effort
                    }
                  } else {
                    logWarning(`Cannot save session name - currentSessionName: "${currentSessionName}", object_id: ${object_id}`, "Chatbot2", undefined);
                  }
                  
                  // CRITICAL: Migrate messages from temp session to real session
                  if (tempSessionId) {
                    // Get messages from temp session
                    const tempMessages = chatManager.getMessagesForChat(tempSessionId);
                    // Migrate them to real session
                    for (const msg of tempMessages) {
                      if (msg.role === 'user') {
                        appendUserMessage(responseData.chat_session_id, msg.content, { 
                          customer: customer_code, 
                          selectionType: currentSelectionType, 
                          ids: currentIds 
                        }, msg.chat_qa_id || qaId);
                      }
                    }
                    
                    // Migrate context mapping
                    const tempMapping = qaIndexMappings.get(tempSessionId);
                    if (tempMapping) {
                      if (!qaIndexMappings.has(responseData.chat_session_id)) {
                        qaIndexMappings.set(responseData.chat_session_id, new Map());
                      }
                      const realMapping = qaIndexMappings.get(responseData.chat_session_id)!;
                      for (const [index, contextKey] of tempMapping) {
                        realMapping.set(index, contextKey);
                      }
                      saveChatContexts();
                    }
                    
                    // Clean up temp session tracking
                    qaIndexMappings.delete(tempSessionId);
                    requestIdToTempSessionId.delete(currentRequestId);
                  } else {
                    // Fallback: if no temp session, create the message now
                    const questionText = requestIdToQuestion.get(currentRequestId);
                    if (questionText) {
                      // Ensure context is stored
                      if (!chatContexts.has(chat_context_key)) {
                        const contextItems: string[] = [];
                        if (currentSelectionType === "projects") {
                          const selected = get(selectedObjects);
                          const projectNames = (selected.projects || [])
                            .map((p: any) => p.project_name || p.project_id)
                            .filter((name: string) => name);
                          contextItems.push(...projectNames);
                        } else {
                          const selected = get(selectedObjects);
                          const documentNames: string[] = [];
                          (selected.documents || []).forEach((d: any) => {
                            const name = d.doc_name || "";
                            if (name) documentNames.push(name);
                          });
                          contextItems.push(...documentNames);
                        }
                        
                        if (contextItems.length > 0) {
                          chatContexts.set(chat_context_key, { 
                            type: currentSelectionType || "documents", 
                            items: contextItems 
                          });
                          saveChatContexts();
                        }
                      }
                      
                      // Store context mapping
                      if (!qaIndexMappings.has(responseData.chat_session_id)) {
                        qaIndexMappings.set(responseData.chat_session_id, new Map());
                      }
                      qaIndexMappings.get(responseData.chat_session_id)!.set(0, chat_context_key);
                      saveChatContexts();
                      
                      // Add user message
                      appendUserMessage(responseData.chat_session_id, questionText, { 
                        customer: customer_code, 
                        selectionType: currentSelectionType, 
                        ids: currentIds 
                      }, qaId);
                      
                      requestIdToQuestion.delete(currentRequestId);
                    }
                  }
                  
                  // CRITICAL: Set current chat to real session
                  chatManager.setCurrentChat(responseData.chat_session_id, currentSelectionType);
                  
                  // Start chat (thinking state should already be active from temp session)
                  chatManager.startChat(responseData.chat_session_id, currentRequestId);
                  
                  await tick();
                  
                  // Sync to real session
                  await syncChats(responseData.chat_session_id);
                }
              } else {
                // Existing session - just update chat manager
                chatManager.setCurrentChat(responseData.chat_session_id, currentSelectionType);
              }
            }
          }
          
          // If the backend returned JSON (not streaming), we need to manually add the Q&A pair
          // because useChat with streamProtocol: "text" expects a streaming response and onFinish might not be called
          if (responseData.answer && currentRequestId) {
            // Find which chat context this request belongs to
            const requestContext = Array.from(
              chatManager.getAllChatContexts().entries(),
            ).find(([id, ctx]) => ctx.requestId === currentRequestId);
            
            if (requestContext) {
              const [chatId, context] = requestContext;
              const chat_context_key = requestIdToContextKey.get(currentRequestId);
              
              // Calculate duration and timestamp
              const startTime = requestStartTimes.get(currentRequestId);
              const endTime = Date.now();
              const duration = startTime ? Math.round((endTime - startTime) / 1000) : 0;
              const timestamp = new Date(endTime);
              
              // CRITICAL: Use the session ID from responseData if available, otherwise fall back to currentChatSessionId
              // This ensures we store the answer in the correct session even if the session ID changed
              const sessionId = responseData.chat_session_id || currentChatSessionId || currentChatId;
              if (!sessionId) {
                logError(new Error("No chat_session_id available for Q&A storage"), "Chatbot2", undefined);
                return;
              }
              
              // If session ID from response is different from current, update it
              if (responseData.chat_session_id && responseData.chat_session_id !== currentChatSessionId) {
                currentChatSessionId = responseData.chat_session_id;
                currentChatId = responseData.chat_session_id;
                setPersistedSessionId(responseData.chat_session_id);
                
                // Ensure chat manager is set to the correct session
                chatManager.setCurrentChat(responseData.chat_session_id, currentSelectionType);
              }
              
              // Get chat_qa_id for this Q&A pair (from request mapping)
              const qaId = requestIdToQaId.get(currentRequestId);
              if (!qaId) {
                logError(new Error(`No chat_qa_id found for request ${currentRequestId} - cannot store answer`), "Chatbot2", undefined);
                return;
              }
              
              // Extract answer from responseData - handle all possible cases:
              // 1. responseData.answer is a plain string (the answer text)
              // 2. responseData.answer is a JSON string containing {answer: "...", ...}
              // 3. responseData.answer is an object with {answer: "...", ...}
              // 4. responseData itself might be the full JSON response (if answer field doesn't exist)
              let answerText: string;
              
              // First, check if responseData.answer exists
              if (responseData.answer !== undefined && responseData.answer !== null) {
                answerText = responseData.answer;
              } else {
                // If responseData.answer doesn't exist, maybe responseData itself is the answer object
                // Check if responseData has an answer field
                if (responseData.answer !== undefined) {
                  answerText = responseData.answer;
                } else {
                  logError(new Error(`responseData.answer is undefined/null, responseData: ${JSON.stringify(responseData).substring(0, 200)}`), "Chatbot2", undefined);
                  return;
                }
              }
              
              // Handle string case
              if (typeof answerText === 'string') {
                // Check if it's a JSON string (starts with { or [)
                const trimmed = answerText.trim();
                if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                  try {
                    const parsed = JSON.parse(answerText);
                    // If parsed result has an answer field, use it
                    if (parsed.answer && typeof parsed.answer === 'string') {
                      answerText = parsed.answer;
                    } else if (typeof parsed === 'string') {
                      // Parsed result is a string (unlikely but handle it)
                      answerText = parsed;
                    } else {
                      // Parsed result is an object but no answer field - this shouldn't happen, but log it
                      logError(new Error(`onResponse - JSON string parsed but has no answer field: ${JSON.stringify(parsed)}`), "Chatbot2", undefined);
                      answerText = JSON.stringify(parsed);
                    }
                  } catch (e) {
                    // Not valid JSON, use as-is (it's plain text)
                    logError(e, "Chatbot2", undefined, { message: `answerText is plain text (not valid JSON): ${e}` });
                  }
                }
              } 
              // Handle object case
              else if (typeof answerText === 'object' && answerText !== null) {
                if (answerText.answer && typeof answerText.answer === 'string') {
                  answerText = answerText.answer;
                } else {
                  // Object but no answer field - this shouldn't happen, log it
                  logError(new Error(`Object has no answer field: ${JSON.stringify(answerText)}`), "Chatbot2", undefined);
                  answerText = JSON.stringify(answerText);
                }
              }
              
              // Final validation - ensure answerText is a string
              if (typeof answerText !== 'string') {
                logError(new Error(`answerText is not a string after processing, type: ${typeof answerText}`), "Chatbot2", undefined);
                answerText = String(answerText);
              }
              
             
              // Append the assistant answer with chat_qa_id
              // CRITICAL: Store the extracted plain text answer, not JSON
              chatManager.appendAssistantMessage(sessionId, answerText, qaId);
              
              // Verify what was stored
              const storedMessages = chatManager.getMessagesForChat(sessionId);
              const storedAnswer = storedMessages.find(msg => msg.chat_qa_id === qaId && msg.role === 'assistant');
              if (!storedAnswer) {
                logError(new Error(`Could not find stored answer for qaId ${qaId}`), "Chatbot2", undefined);
              }
              
              // Mark as newly added (should stream) - MUST be before completeChat
              newlyAddedAnswers.add(qaId);
              // Track qaId to requestId mapping so we can clear thinking state when streaming starts
              if (currentRequestId) {
                qaIdToRequestId.set(qaId, currentRequestId);
              }
              
              // Extract route from responseData if available
              let route = responseData.route || null;
              if (route && typeof route === 'string') {
                try {
                  route = JSON.parse(route);
                } catch (e) {
                  logError(e, "Chatbot2", undefined, { message: `Failed to parse route JSON string for qaId ${qaId}` });
                }
              }
              
              // Store metadata using chat_qa_id (including route)
              messageMetadata.set(qaId, { timestamp, duration, route: route });
              saveMessageMetadata();
              
              // Clean up request tracking
              requestStartTimes.delete(currentRequestId);
              requestIdToAnswerAdded.delete(currentRequestId);
              
              // Sync messages to UI FIRST (this will start streaming while thinking state is still active)
              // Use the session ID from responseData to ensure we sync the correct session
              const syncSessionId = responseData.chat_session_id || sessionId;
              await syncChats(syncSessionId);
              
              // DO NOT clear thinking state here - let it clear when streaming actually starts
              // The thinking state will be cleared when startStreaming begins showing content
              // This ensures the animation is visible until the answer actually appears
              
              // Mark that answer was added to prevent onFinish from adding it again
              requestIdToAnswerAdded.set(currentRequestId, true);
            }
          }
        }
      } catch (error) {
        // Response might not be JSON (backward compatibility with streaming)
        logError(error, "Chatbot2", undefined, { message: `Could not parse response as JSON (might be streaming)` });
      }
      // isGenerating is now reactive and managed by chatManager
    },
    onFinish: async (message) => {
      // SIMPLIFIED: Just store the answer and sync messages
      if (!currentRequestId) return;
      
      const sessionId = currentChatSessionId || currentChatId;
      
      // Check if answer was already added in onResponse (for JSON responses)
      if (requestIdToAnswerAdded.get(currentRequestId)) {
        // Still notify ChatHistory that the question was completed, even if answer was already added
        if (sessionId) {
          const qaId = requestIdToQaId.get(currentRequestId);
          if (qaId) {
            // Get timestamp from metadata if available, otherwise use current time
            const metadata = messageMetadata.get(qaId);
            const timestamp = metadata?.timestamp || new Date();
            chatSessionStore.notifyQuestionAdded(sessionId, timestamp instanceof Date ? timestamp.toISOString() : timestamp);
          }
        }
        return;
      }
      
      if (!sessionId) {
        logError(new Error("No sessionId available"), "Chatbot2", undefined);
        return;
      }
      
      // Get chat_qa_id for this Q&A pair
      const qaId = requestIdToQaId.get(currentRequestId);
      if (!qaId) {
        logError(new Error(`No chat_qa_id found for request ${currentRequestId}`), "Chatbot2", undefined);
        return;
      }
      
      // Parse message content - it might be JSON with {answer, ...} or plain text
      // Handle all possible cases:
      // 1. message.content is a plain string (the answer text)
      // 2. message.content is a JSON string containing {answer: "...", ...}
      // 3. message.content is an object with {answer: "...", ...}
      let answerContent = message.content;
      
      if (answerContent === undefined || answerContent === null) {
        logError(new Error("message.content is undefined/null"), "Chatbot2", undefined);
        return;
      }
      
      // Handle string case
      if (typeof answerContent === 'string') {
        // Check if it's a JSON string (starts with { or [)
        const trimmed = answerContent.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(answerContent);
            // If parsed result has an answer field, use it
            if (parsed.answer && typeof parsed.answer === 'string') {
              answerContent = parsed.answer;
            } else if (typeof parsed === 'string') {
              // Parsed result is a string (unlikely but handle it)
              answerContent = parsed;
            } else {
              // Parsed result is an object but no answer field - stringify it
              answerContent = JSON.stringify(parsed);
              logWarning(`onFinish - JSON string has no answer field, using stringified JSON`, "Chatbot2", undefined);
            }
          } catch (error) {
            // Not valid JSON, use as-is (it's plain text)
            logError(error, "Chatbot2", undefined, { message: `message.content is plain text (not JSON)` });
          }
        }
      } 
      // Handle object case
      else if (typeof answerContent === 'object' && answerContent !== null) {
        if (answerContent.answer && typeof answerContent.answer === 'string') {
          answerContent = answerContent.answer;
        } else {
          // Object but no answer field - stringify it
          answerContent = JSON.stringify(answerContent);
        }
      }
      
      // Final validation - ensure answerContent is a string
      if (typeof answerContent !== 'string') {
        answerContent = String(answerContent);
      }
      
      // Calculate duration and timestamp
      const startTime = requestStartTimes.get(currentRequestId);
      const endTime = Date.now();
      const duration = startTime ? Math.round((endTime - startTime) / 1000) : 0;
      const timestamp = new Date(endTime);
      
      // Store the assistant answer with chat_qa_id
      chatManager.appendAssistantMessage(sessionId, answerContent, qaId);
      
      // Mark as newly added (should stream) - MUST be before completeChat
      newlyAddedAnswers.add(qaId);
      
      // Extract route from message if available (for streaming responses)
      let route = null;
      if (typeof message.content === 'string') {
        try {
          const parsed = JSON.parse(message.content);
          if (parsed.route) {
            route = parsed.route;
          }
        } catch (e) {
          // Not JSON or no route field
        }
      } else if (message.content && typeof message.content === 'object' && message.content.route) {
        route = message.content.route;
      }
      
      // Store metadata (route will be loaded from database when session is reloaded if not available here)
      messageMetadata.set(qaId, { timestamp, duration, route: route });
      saveMessageMetadata();
      
      // Mark that answer was added
      requestIdToAnswerAdded.set(currentRequestId, true);
      
      // Track qaId to requestId mapping for clearing thinking state when streaming starts
      if (currentRequestId) {
        qaIdToRequestId.set(qaId, currentRequestId);
      }
      
      // Sync messages to UI FIRST (this will start streaming while thinking state is still active)
      syncChats(sessionId);
      
      // DO NOT clear thinking state here - let startStreaming handle it
      // This ensures the animation stays visible until content actually appears
      // Add a fallback timeout to clear thinking state if streaming doesn't start (safety net)
      if (currentRequestId) {
        setTimeout(() => {
          // Only clear if still in thinking state (streaming should have cleared it by now)
          if (chatManager.isChatGenerating(sessionId)) {
            chatManager.completeChat(sessionId, currentRequestId);
          }
        }, 2000); // 2 second fallback timeout
      }
      
      // Notify ChatHistory that a question was fully added (response complete)
      // This ensures the date is updated with the actual response time
      chatSessionStore.notifyQuestionAdded(sessionId, timestamp.toISOString());
      
      // Get chat_context_key and store mapping if needed
      // Note: For existing sessions, the mapping was already stored in handleSubmitWithLogging
      // This is only needed for new sessions where the mapping might not be set yet
      const chat_context_key = requestIdToContextKey.get(currentRequestId);
      if (chat_context_key) {
        // Check if mapping already exists (for existing sessions it should)
        if (!qaIndexMappings.has(sessionId) || !qaIndexMappings.get(sessionId)?.has(0)) {
          // Count user questions to determine Q&A index
          const allQAs = chatManager.getMessagesForChat(sessionId);
          let userQuestionIndex = 0;
          for (const m of allQAs) {
            if (m.role === "user") {
              if (m.chat_qa_id === qaId) {
                // Found the question - store mapping if not already set
                if (!qaIndexMappings.has(sessionId)) {
                  qaIndexMappings.set(sessionId, new Map());
                }
                const mapping = qaIndexMappings.get(sessionId)!;
                if (!mapping.has(userQuestionIndex)) {
                  mapping.set(userQuestionIndex, chat_context_key);
                  saveChatContexts(); // Saves both chatContexts and qaIndexMappings
                }
                break;
              }
              userQuestionIndex++;
            }
          }
        }
        
        // Ensure context is stored
        if (!chatContexts.has(chat_context_key)) {
          const contextItems: string[] = [];
          if (currentSelectionType === "projects") {
            const selected = get(selectedObjects);
            const projectNames = (selected.projects || [])
              .map((p: any) => p.project_name || p.project_id)
              .filter((name: string) => name);
            contextItems.push(...projectNames);
          } else {
            const selected = get(selectedObjects);
            (selected.documents || []).forEach((d: any) => {
              const name = (d as any).doc_filename || d.file_name || d.doc_title || "";
              if (name) contextItems.push(name);
            });
          }
          
          if (contextItems.length > 0) {
            chatContexts.set(chat_context_key, { 
              type: currentSelectionType || "documents", 
              items: contextItems 
            });
            saveChatContexts();
          }
        }
      }
      
      // Clean up
      requestStartTimes.delete(currentRequestId);
      requestIdToAnswerAdded.delete(currentRequestId);
      
      // Sync messages to UI (this will show the answer and hide thinking state)
      syncChats(sessionId);
      
      currentRequestId = null;
    },
    onError: (error) => {
      // Complete the chat request for the chat that actually generated this response
      if (currentRequestId) {
        // Find which chat context this request belongs to
        const requestContext = Array.from(
          chatManager.getAllChatContexts().entries(),
        ).find(([id, ctx]) => ctx.requestId === currentRequestId);

        if (requestContext) {
          const [chatId, context] = requestContext;
          chatManager.completeChat(chatId, currentRequestId);
          
          // Clean up request start time on error
          requestStartTimes.delete(currentRequestId);
        }

        currentRequestId = null;
      }
    },
  });

  // Helper function to calculate and store context key for current selection
  function initializeContextKey(): void {
    try {
      const selected = get(selectedObjects);
      
      // Determine if we're in project mode using the same logic as ViewResults.svelte
      const inProjectMode = isProjectMode(selected);
      
      // Normalize selections from selectedObjects
      // Only include projects if we're actually in project mode
      const normalizedProjects = inProjectMode ? normalizeProjects(selected.projects || []) : [];
      const selectedDocs = selected.documents || [];
      
      // Deduplicate documents by doc_key
      const seenDocKeys = new Set<string>();
      const uniqueSelectedDocs = selectedDocs.filter((doc: any) => {
        if (seenDocKeys.has(doc.doc_key)) {
          return false;
        }
        seenDocKeys.add(doc.doc_key);
        return true;
      });
      
      const normalizedDocuments = normalizeDocuments(uniqueSelectedDocs);
      
      const selectionType: "projects" | "documents" =
        inProjectMode ? "projects" : "documents";
      
      // Extract doc_key values for UUID generation
      const ids =
        selectionType === "projects"
          ? normalizedProjects
          : normalizedDocuments.map((d) => {
              if (!d.doc_key || typeof d.doc_key !== "string" || d.doc_key.trim() === "") {
                logWarning(`Document missing doc_key, skipping context init`, "Chatbot2", undefined);
                return null;
              }
              return d.doc_key.trim();
            }).filter((id): id is string => id !== null);
      
      if (ids.length === 0) {
        // No selection, skip context initialization
        return;
      }
      
      // Store context items for display
      const contextItems: string[] = [];
      if (selectionType === "projects") {
        const projectNames = (selected.projects || [])
          .map((p: any) => p.project_name || p.project_id)
          .filter((name: string) => name);
        contextItems.push(...projectNames);
      } else {
        for (const doc of normalizedDocuments) {
          if (doc.doc_name && typeof doc.doc_name === "string" && doc.doc_name.trim() !== "") {
            contextItems.push(doc.doc_name.trim());
          }
        }
      }
      
      if (contextItems.length === 0) {
        return;
      }
      
      // Generate deterministic UUID from document/project IDs using UUID v5
      const sortedIds = [...ids].sort();
      const hashInput = selectionType === "projects" 
        ? `projects_${sortedIds.join("_")}`
        : `documents_${sortedIds.join("_")}`;
      
      // Use UUID v5 with same namespaces as backend
      const NAMESPACE_DOCUMENTS = "6ba7b810-9dad-11d1-80b4-00c04fd430c8";
      const NAMESPACE_PROJECTS = "6ba7b811-9dad-11d1-80b4-00c04fd430c8";
      
      const chat_context_key = uuidv5(hashInput, selectionType === "projects" ? NAMESPACE_PROJECTS : NAMESPACE_DOCUMENTS);
      
      // Store context immediately so it's available when first question is asked
      chatContexts.set(chat_context_key, { type: selectionType, items: contextItems });
      saveChatContexts();
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error initializing context key" });
      // Don't throw - this is best effort
    }
  }

  async function restartChat(e?: Event, clearPersisted: boolean = true) {
    if (e) {
      e.preventDefault();
    }
    // Clear in-memory and (optionally) persisted messages for this chat
    if (clearPersisted) {
      try {
        if (currentChatId) {
          clearChatMessages(currentChatId);
        }
      } catch {}
    }
    setMessages([]); // clear UI; restoration happens only when context changes or user types again
    $input = "";

    // Clear the current chat state and session
    chatManager.setCurrentChat(null);
    currentRequestId = null;
    currentChatSessionId = null; // Clear session ID so new session is created on next question
    setPersistedSessionId(null); // Also clear persisted value
    const newName = getNewChatName(); // Use incremented counter for new chat name
    currentSessionName = newName;
    currentSessionStarted = null;
    
    // CRITICAL: Clear lastSyncedQAs so reactive statement doesn't use stale data for new session
    lastSyncedQAs = [];
    lastSyncedSessionId = null;

    // CRITICAL: Initialize context key for current selection so it's ready for first question
    initializeContextKey();

    setTimeout(scrollToBottom, 0);
  }

  // Watch for new chat signal from hyperlink clicks
  let lastNewChatSignal = 0;
  $: {
    const signalValue = $newChatSignal;
    if (signalValue !== lastNewChatSignal && signalValue > 0) {
      lastNewChatSignal = signalValue;
      restartChat(undefined, true);
    }
  }

  async function handleSubmitWithLogging( e: Event, fromSuggestion: boolean = false ) {
    e.preventDefault();
    try {
      // Get session_id from localStorage (same as api-client.ts)
      const sessionId = localStorage.getItem("sessionId");

      // Temporary: Set a test session ID if none exists (for debugging)
      const finalSessionId = sessionId || "test-session-" + Date.now();

      // CRITICAL: Use selectedObjects store for current context, not props
      // This ensures we use the actual selected documents/projects, not stale props
      const selected = get(selectedObjects);
      
      // Determine if we're in project mode using the same logic as ViewResults.svelte
      const inProjectMode = isProjectMode(selected);
      
      // Normalize selections from selectedObjects
      // Only include projects if we're actually in project mode
      const normalizedProjects = inProjectMode ? normalizeProjects(selected.projects || []) : [];
     
      // Normalize documents - NO FALLBACKS, strict validation
      const selectedDocs = (selected.documents || []).map((selectedDoc: any, index: number) => {
        // Validate doc_key - throw error if missing
        if (!selectedDoc.doc_key || typeof selectedDoc.doc_key !== "string" || selectedDoc.doc_key.trim() === "") {
          const errorMsg = `Document at index ${index} is missing required field 'doc_key'. Document: ${JSON.stringify(selectedDoc)}`;
          logError(new Error(errorMsg), "Chatbot2", undefined);
          throw new Error(errorMsg);
        }
        const doc_key = selectedDoc.doc_key.trim();
        
        // Extract doc_name - throw error if not available
        let doc_name: string;
        try {
          doc_name = extractDocName(selectedDoc);
        } catch (error) {
          const errorMsg = `Document at index ${index}: ${error instanceof Error ? error.message : String(error)}`;
          logError(new Error(errorMsg), "Chatbot2", undefined);
          throw new Error(errorMsg);
        }
        
        // Validate project_id - throw error if missing
        if (!selectedDoc.project_id || typeof selectedDoc.project_id !== "string" || selectedDoc.project_id.trim() === "") {
          const errorMsg = `Document at index ${index} is missing required field 'project_id'`;
          logError(new Error(errorMsg), "Chatbot2", undefined);
          throw new Error(errorMsg);
        }
        const project_id = selectedDoc.project_id.trim();
        
        // Derive file_name from doc_key and doc_name - throws error if extension cannot be determined
        let file_name: string;
        try {
          file_name = deriveFileName(doc_key, doc_name);
        } catch (error) {
          const errorMsg = `Document at index ${index}: ${error instanceof Error ? error.message : String(error)}`;
          logError(new Error(errorMsg), "Chatbot2", undefined);
          throw new Error(errorMsg);
        }
        
        const result = {
          doc_key: doc_key,
          doc_name: doc_name,
          file_name: file_name,
          project_id: project_id,
        };

        return result;
      }).filter((d: any) => d && d.doc_key && d.doc_key.length > 0);
      
      // Deduplicate documents by doc_key to prevent sending duplicates
      const seenDocKeys = new Set<string>();
      const uniqueSelectedDocs = selectedDocs.filter((doc: any) => {
        if (seenDocKeys.has(doc.doc_key)) {
          logWarning(`Duplicate document detected, skipping: ${doc}`, "Chatbot2", undefined);
          return false;
        }
        seenDocKeys.add(doc.doc_key);
        return true;
      });
      
      const normalizedDocuments = normalizeDocuments(uniqueSelectedDocs);
      
      const selectionType: "projects" | "documents" =
        inProjectMode ? "projects" : "documents";
      
      // Extract doc_key values for UUID generation
      const ids =
        selectionType === "projects"
          ? normalizedProjects
          : normalizedDocuments.map((d) => {
              if (!d.doc_key || typeof d.doc_key !== "string" || d.doc_key.trim() === "") {
                throw new Error(`Document is missing required field 'doc_key' for UUID generation: ${JSON.stringify(d)}`);
              }
              return d.doc_key.trim();
            });
      
      // Store context for this chat_id to display in UI
      const contextItems: string[] = [];
      if (selectionType === "projects") {
        // Get project names
        const selected = get(selectedObjects);
        const projectNames = (selected.projects || [])
          .map((p: any) => p.project_name || p.project_id)
          .filter((name: string) => name);
        contextItems.push(...projectNames);
      } else {
        // Get document names from normalized documents - use doc_name
        for (const doc of normalizedDocuments) {
          if (!doc.doc_name || typeof doc.doc_name !== "string" || doc.doc_name.trim() === "") {
            throw new Error(`Document is missing required field 'doc_name' for context display: ${JSON.stringify(doc)}`);
          }
          contextItems.push(doc.doc_name.trim());
        }
      }
      
      // Generate deterministic UUID from document/project IDs using UUID v5
      // This creates a single UUID from any number of document IDs, even thousands
      // UUID v5 is deterministic - same input always produces same UUID
      // Must match backend logic exactly
      const sortedIds = [...ids].sort();
      const hashInput = selectionType === "projects" 
        ? `projects_${sortedIds.join("_")}`
        : `documents_${sortedIds.join("_")}`;
      
      // Use UUID v5 with same namespaces as backend
      // Namespace UUIDs must match backend exactly
      const NAMESPACE_DOCUMENTS = "6ba7b810-9dad-11d1-80b4-00c04fd430c8";
      const NAMESPACE_PROJECTS = "6ba7b811-9dad-11d1-80b4-00c04fd430c8";
      
      const chat_context_key = uuidv5(hashInput, selectionType === "projects" ? NAMESPACE_PROJECTS : NAMESPACE_DOCUMENTS);
      
      // Store context immediately with deterministic chat_context_key
      // No temporary storage needed - chat_context_key is deterministic
      chatContexts.set(chat_context_key, { type: selectionType, items: contextItems });
      saveChatContexts(); // Persist to localStorage
      
      // Update last question context
      lastQuestionContextId = chat_context_key;

      // CRITICAL: Server generates the session UUID - we don't create any IDs or keys
      // For new sessions, we don't store messages locally until server returns the real session_id
      const hasExistingSession = !!currentChatSessionId;
      
      // Generate a unique request ID for tracking
      currentRequestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Generate chat_qa_id (UUID v4) on client for this Q&A pair
      const chat_qa_id = uuidv4();
      
      // Store chat_qa_id mapped to requestId so we can retrieve it in response handlers
      requestIdToQaId.set(currentRequestId, chat_qa_id);
      
      // Store question text for new sessions (so we can store user message when server returns session_id)
      if (!hasExistingSession) {
        requestIdToQuestion.set(currentRequestId, $input);
      }
      
      // Track request start time for duration calculation
      requestStartTimes.set(currentRequestId, Date.now());
      
      // Update context tracking (for display purposes)
      currentSelectionType = selectionType;
      currentIds = ids;
      
      // Build payload for chat2Ask
      const payload: any = {
        question: $input,
        object_id: object_id,
        customer: customer_code,
        // IMPORTANT: When documents are selected, do NOT send stage (per requirements: "stage cannot be selected" when documents are selected)
        // Only include stage when using project selection (selectionType === "projects")
        stage: selectionType === "projects" ? stage : undefined,
        chat_qa_id: chat_qa_id, // Client-generated UUID for this Q&A pair
      };
      
      // Store chat_context_key mapping for request tracking (needed for response handlers)
      requestIdToContextKey.set(currentRequestId, chat_context_key);
      
      // Only include chat_session_id if it's an existing session from the database
      // For new sessions, server will create the session and return the session_id
      if (hasExistingSession) {
        payload.chat_session_id = currentChatSessionId;
        // For existing sessions, we can store the user message immediately
        ensureChatRecord(currentChatSessionId, { customer: customer_code, selectionType, ids });
        
        // CRITICAL: Set current chat BEFORE starting chat so reactive state updates correctly
        chatManager.setCurrentChat(currentChatSessionId, selectionType);
        
        // Store chat_context_key mapping BEFORE adding user message so context bar shows immediately
        // Count existing user questions to determine the index for this new question
        const existingMessages = chatManager.getMessagesForChat(currentChatSessionId);
        const userQuestionIndex = existingMessages.filter(m => m.role === "user").length;
        
        if (!qaIndexMappings.has(currentChatSessionId)) {
          qaIndexMappings.set(currentChatSessionId, new Map());
        }
        const mapping = qaIndexMappings.get(currentChatSessionId)!;
        mapping.set(userQuestionIndex, chat_context_key);
        saveChatContexts(); // Persist the mapping - CRITICAL: must be saved before syncChats
        
        // Add user message FIRST (so it's in the store when we sync)
        appendUserMessage(currentChatSessionId, $input, { customer: customer_code, selectionType, ids }, chat_qa_id);
        
        // Start chat (sets thinking state) - must be called AFTER setCurrentChat and appendUserMessage
        chatManager.startChat(currentChatSessionId, currentRequestId);
        
        // Use tick() to ensure all reactive updates are applied before syncing
        await tick();
        
        // Additional tick to ensure thinking state has propagated through reactive stores
        await tick();
        
        // Sync immediately to show context bar, question, and thinking state
        syncChats(currentChatSessionId);
        
        // Note: We don't notify ChatHistory here because the question might not be saved yet.
        // We'll notify in onFinish after the response completes, which ensures the backend has saved it.
      }
      // If no existing session, don't send chat_session_id - server will create new session
      // Don't store messages locally yet - wait for server to return session_id
      
      if (selectionType === "projects") {
        payload.projects = normalizedProjects;
      } else {
        // Send documents with required structure: doc_key, doc_name, file_name, project_id
        // normalizedDocuments already has this structure from the normalization step
        payload.documents = normalizedDocuments;
      }

      // Add request ID to payload for backend tracking
      payload.request_id = currentRequestId;

      // CRITICAL: For new sessions, set up a temporary session structure so we can display
      // the question with context immediately. The server will return the real session_id later.
      if (!hasExistingSession) {
        // Create a temporary session ID that we'll replace when server responds
        const tempSessionId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Set up the chat manager with temporary session
        chatManager.setCurrentChat(tempSessionId, selectionType);
        
        // Store context mapping for index 0 (first question)
        if (!qaIndexMappings.has(tempSessionId)) {
          qaIndexMappings.set(tempSessionId, new Map());
        }
        qaIndexMappings.get(tempSessionId)!.set(0, chat_context_key);
        saveChatContexts();
        
        // Store user message immediately with proper chat_qa_id and chat_context_key
        appendUserMessage(tempSessionId, $input, { customer: customer_code, selectionType, ids }, chat_qa_id);
        
        // Start thinking state
        chatManager.startChat(tempSessionId, currentRequestId);
        
        // Track that this is a temporary session so we can migrate it later
        requestIdToTempSessionId.set(currentRequestId, tempSessionId);
      }

      handleSubmit(e, {
        body: payload,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          ...(finalSessionId ? { "x-session-id": finalSessionId } : {}),
        },
      });
      
      // CRITICAL: Immediately sync to override useChat's auto-generated message
      // This ensures context bubble, question, and thinking animation show right away
      if (hasExistingSession && currentChatSessionId) {
        // Wait a tick for useChat to add its message, then override it
        await tick();
        await syncChats(currentChatSessionId);
      } else {
        // For new sessions, sync with temporary session ID
        const tempSessionId = requestIdToTempSessionId.get(currentRequestId);
        if (tempSessionId) {
          await tick();
          await syncChats(tempSessionId);
        }
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error submitting message" });
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if ($input.trim().length > 0) {
        handleSubmitWithLogging(event);
      }
    }
  }

  function scrollToBottom() {
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  $: if ($messages) {
    setTimeout(scrollToBottom, 0);
  }

  // SIMPLIFIED: Single function to sync messages from chatManager to useChat
  // This is the ONLY place that should call setMessages() (except explicit handlers)
  // Store the last synced QAs so we can compare against useChat's QAs
  let lastSyncedQAs: any[] = [];
  
  async function syncChats(sessionId: string | null) {
      if (!sessionId) {
        setMessages([]);
        lastSyncedQAs = [];
        lastSyncedSessionId = null;
        return;
      }

    loadMessageMetadata();
    
    // Get Q&A index mapping for this session to restore chat_context_key values
    const qaIndexMapping = qaIndexMappings.get(sessionId);
    
    // Get all Q&A pairs from chatManager
    const allQAs = chatManager.getMessagesForChat(sessionId);
    
    // Filter out messages without chat_qa_id (these are from useChat and shouldn't be displayed)
    const validQAs = allQAs.filter((m) => {
      if (!m.chat_qa_id) {
        logWarning(`syncChats - Filtering out message without chat_qa_id: ${m}`, "Chatbot2", undefined);
        return false;
      }
      return true;
    });
    
    // Build messages with chat_context_key
    // CRITICAL: Build a map of chat_qa_id -> chat_context_key for user messages
    // This allows assistant messages to easily look up their context key
    let userQuestionIndex = 0;
    const qaIdToContextKey = new Map<string, string>(); // Track context keys by chat_qa_id
    
    // First pass: map chat_qa_id to context keys for user messages
    allQAs.forEach((m) => {
      if (m.role === "user" && m.chat_qa_id) {
        if (qaIndexMapping) {
          const contextKey = qaIndexMapping.get(userQuestionIndex);
          if (contextKey) {
            qaIdToContextKey.set(m.chat_qa_id, contextKey);
          }
        }
        userQuestionIndex++;
      }
    });
    
    // Second pass: build synced messages, using context keys from the map
    userQuestionIndex = 0; // Reset for validQAs iteration
    const syncedMessages = validQAs.map((m, validIndex) => {
      let qaId = m.chat_qa_id;
      
      // If message is missing chat_qa_id, try to infer it from the content (for assistant messages that might be JSON)
      // Also extract the answer if content is JSON
      // NOTE: This happens BEFORE the main content parsing below, so we extract early
      // We'll store the extracted content separately to avoid modifying the original message
      let extractedContent: string | null = null;
      if (m.role === "assistant" && typeof m.content === 'string') {
        const contentStr = m.content.trim();
        // Only try to parse if it looks like JSON (starts with { or [)
        if (contentStr.startsWith('{') || contentStr.startsWith('[')) {
          try {
            const parsed = JSON.parse(m.content);
            if (parsed.chat_qa_id && !qaId) {
              qaId = parsed.chat_qa_id;
            }
            // If content is JSON with an answer field, extract the answer
            if (parsed.answer && typeof parsed.answer === 'string') {
              extractedContent = parsed.answer;
            } else if (typeof parsed === 'string') {
              // Unlikely but handle it
              extractedContent = parsed;
            }
          } catch (e) {
            // Not valid JSON, content is already the answer (plain text)
            logWarn("syncChats - Content is not valid JSON, using as-is", "Chatbot2", undefined, { error: e });
          }
        }
      }
      
      if (!qaId) {
        return null;
      }
      
      const metadata = messageMetadata.get(qaId);
      
      // Determine chat_context_key for this Q&A pair
      let qaContextKey: string | undefined = undefined;
      
      if (m.role === "user") {
        // For user questions, get from mapping
        if (qaIndexMapping) {
          qaContextKey = qaIndexMapping.get(userQuestionIndex);
        }
        userQuestionIndex++;
      } else if (m.role === "assistant") {
        // For assistant answers, use the same chat_qa_id to look up context key
        // User and assistant messages share the same chat_qa_id (they're a Q&A pair)
        qaContextKey = qaIdToContextKey.get(qaId);
        
        // Fallback: if not found, try to find the user message with same chat_qa_id
        if (!qaContextKey) {
          const userMsg = allQAs.find(msg => msg.role === "user" && msg.chat_qa_id === qaId);
          if (userMsg && userMsg.chat_qa_id) {
            qaContextKey = qaIdToContextKey.get(userMsg.chat_qa_id);
          }
        }
      }
      
      // Convert timestamp to Date if needed
      let timestamp: Date | undefined = undefined;
      if (metadata?.timestamp) {
        const ts = metadata.timestamp as any;
        timestamp = ts instanceof Date ? ts : new Date(ts);
      } else if (m.timestamp) {
        const ts = m.timestamp as any;
        timestamp = ts instanceof Date ? ts : new Date(ts);
      }
      
      // Parse content if it's JSON (for assistant messages that might have JSON content)
      // This handles cases where useChat stored the raw JSON response
      // BUT: Skip parsing if it looks like an error message (to avoid parsing error text as JSON)
      // Use extracted content from early extraction if available, otherwise parse now
      let content: string;
      if (extractedContent !== null) {
        // Early extraction already extracted the answer
        content = extractedContent;

      } else if (m.role === "assistant") {
        // Check if content looks like an error message (plain text, not JSON)
        const contentStr = typeof m.content === 'string' ? m.content : String(m.content);
        const isErrorMessage = contentStr.startsWith('Server error') || 
                               contentStr.startsWith('An error') ||
                               contentStr.startsWith('Error:') ||
                               contentStr.startsWith('**error') ||
                               contentStr.startsWith('An **error');
        
        // Only try to parse JSON if it doesn't look like an error message
        if (!isErrorMessage) {
          try {
            let parsed: any = null;
            
            // Handle different content types
            if (typeof m.content === 'string') {
              // Only try to parse if it looks like JSON (starts with { or [)
              const trimmed = m.content.trim();
              if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                  parsed = JSON.parse(m.content);
                } catch (parseError) {
                  // Not valid JSON, use as-is
                  parsed = null;
                }
              } else {
                // Not JSON, use as-is
                parsed = null;
              }
            } else if (typeof m.content === 'object' && m.content !== null) {
              // Already an object
              parsed = m.content;
            }
            
            // If we have a parsed object, try to extract answer
            if (parsed && typeof parsed === 'object' && parsed.answer !== undefined) {
              if (typeof parsed.answer === 'string') {
                content = parsed.answer;
              } else {
                // Answer is not a string, stringify it
                content = JSON.stringify(parsed.answer);
             }
            } else {
              // No answer field, use original content
              content = typeof m.content === 'string' ? m.content : String(m.content);
            }
          } catch (e) {
            // Not JSON or parse failed, use content as-is
            content = typeof m.content === 'string' ? m.content : String(m.content);
          }
        } else {
          // Error message - use as-is (already plain text)
          content = typeof m.content === 'string' ? m.content : String(m.content);
        }
      } else {
        // Not an assistant message, use content as-is
        content = typeof m.content === 'string' ? m.content : String(m.content);
      }
      
      const syncedQA = {
        id: qaId, // CRITICAL: Use chat_qa_id as the id so useChat recognizes it
        chat_qa_id: qaId,
        chat_session_id: sessionId, // Include session ID for reference
        role: m.role,
        content: content,
        timestamp: timestamp,
        duration: metadata?.duration,
        chat_context_key: qaContextKey,
      };
      
      return syncedQA;
    }).filter((qa) => qa !== null);
    
    // Deduplicate QAs by unique key (chat_qa_id + role) to prevent duplicates
    // Note: User and assistant QAs can share the same chat_qa_id (they're a Q&A pair),
    // so we need to include role in the deduplication key
    const seenKeys = new Set<string>();
    const deduplicatedQAs = syncedMessages.filter((qa) => {
      const qaId = qa.chat_qa_id || qa.id;
      const uniqueKey = `${qaId}_${qa.role}`; // Combine qaId and role for uniqueness
      if (seenKeys.has(uniqueKey)) {
        return false;
      }
      seenKeys.add(uniqueKey);
      return true;
    });
    
    // Start streaming for NEW assistant QAs only (not for redisplayed/historical QAs)
    // Only stream if the QA is:
    // 1. An assistant QA with chat_qa_id
    // 2. Marked as newly added (via appendAssistantMessage during active request)
    // 3. Not already streaming
    // 4. Not already complete
    deduplicatedQAs.forEach((qa) => {
      if (qa.role === "assistant" && qa.chat_qa_id) {
        const qaId = qa.chat_qa_id;
        const isNewlyAdded = newlyAddedAnswers.has(qaId);
        const isAlreadyShown = messagesAlreadyShown.has(qaId);
        const isStreaming = streamingIntervals.has(qaId);
        const isComplete = streamingComplete.has(qaId);
       
        // PRIORITY 1: If it's newly added and not already shown/streaming/complete, start streaming
        if (isNewlyAdded && !isAlreadyShown && !isStreaming && !isComplete) {
          // Initialize streaming state BEFORE starting the interval
          streamingMessages.set(qaId, "");
          // Set interval immediately so streamingIntervals.has(qaId) is true
          startStreaming(qaId, qa.content);
          // Don't remove from newlyAdded yet - let streaming complete first
        } 

        // PRIORITY 3: If it's not newly added (from history), show full text immediately
        else if (!isNewlyAdded) {
          streamingComplete.add(qaId);
          streamingMessages.set(qaId, qa.content);
          newlyAddedAnswers.delete(qaId); // Clean up
        }
        // PRIORITY 4: If it's newly added but already shown (shouldn't happen, but handle gracefully)
        else if (isAlreadyShown) {
          streamingComplete.add(qaId);
          streamingMessages.set(qaId, qa.content);
          newlyAddedAnswers.delete(qaId); // Clean up
        }
      }
    });
    
    // Mark all ASSISTANT QAs as "already shown" after processing (for future redisplay)
    // BUT: Don't mark if they're currently streaming or newly added (let streaming complete first)
    // NOTE: Only mark assistant QAs, not user QAs, since user and assistant share the same chat_qa_id
    deduplicatedQAs.forEach((qa) => {
      if (qa.role === "assistant" && qa.chat_qa_id && !streamingIntervals.has(qa.chat_qa_id) && !newlyAddedAnswers.has(qa.chat_qa_id)) {
        messagesAlreadyShown.add(qa.chat_qa_id);
      }
    });
    
    // CRITICAL: Override useChat's QAs completely with our synced QAs
    // This ensures all QAs have chat_session_id, chat_qa_id, chat_context_key and extracted content (not JSON)
    
    // Store the synced QAs FIRST so reactive statement can use them
    lastSyncedQAs = deduplicatedQAs;
    // CRITICAL: Track which session these QAs belong to, so reactive statement doesn't use stale data
    lastSyncedSessionId = sessionId;
    
    // Use tick to ensure reactive statement sees lastSyncedQAs before we set messages
    await tick();
    
    setMessages(deduplicatedQAs);
    
    // Force another tick to ensure messages are set before useChat can interfere
    await tick();
    
    // Double-check and force correction if useChat interfered
    const currentMessages = $messages || [];
    const currentQAIds = currentMessages.map((qa: any) => qa.chat_qa_id || qa.id).join(",");
    const expectedQAIds = deduplicatedQAs.map((qa: any) => qa.chat_qa_id || qa.id).join(",");
    if (currentQAIds !== expectedQAIds) {
      setMessages(deduplicatedQAs);
    }
    
    setTimeout(scrollToBottom, 0);
  }
  
  // Track the session ID that lastSyncedQAs belongs to, to prevent using stale data
  let lastSyncedSessionId: string | null = null;
  
  // CRITICAL: Reactive statement to continuously correct useChat's QAs array
  // useChat automatically adds QAs with auto-generated IDs, so we need to override them whenever they don't match
  // We use chat_qa_id as the source of truth, not useChat's auto-generated IDs
  // ALWAYS correct immediately when there's a mismatch, but ONLY if lastSyncedQAs belongs to the current session
  $: if (currentChatSessionId && lastSyncedQAs.length > 0 && lastSyncedSessionId === currentChatSessionId) {
    const currentMessages = $messages || [];
    const currentQAIds = currentMessages.map((qa: any) => qa.chat_qa_id || qa.id).join(",");
    const expectedQAIds = lastSyncedQAs.map((qa: any) => qa.chat_qa_id || qa.id).join(",");
    
    // Also check if any user messages are missing chat_context_key (useChat's messages won't have it)
    // This is critical for showing the context bubble on the first question
    const hasMessagesWithoutContext = currentMessages.some((qa: any) => 
      qa.role === 'user' && !qa.chat_context_key && qa.chat_qa_id
    );
    
    // Check if useChat added QAs that don't match our synced QAs
    // This happens because useChat generates its own IDs (like 'GLl7NEc97tMCFV8F') instead of using our chat_qa_id
    if (currentQAIds !== expectedQAIds || hasMessagesWithoutContext) {
      
      // Override useChat's QAs with our synced QAs that have proper IDs and context keys
      // This is safe even during thinking state - we're just correcting the message structure, not clearing thinking
      setMessages(lastSyncedQAs);
    }
  }
  
  // Stream text character by character for teleprompter effect
  function startStreaming(qaId: string, fullText: string) {
    // Clear any existing interval for this message
    if (streamingIntervals.has(qaId)) {
      clearInterval(streamingIntervals.get(qaId)!);
    }
    
    // Initialize with empty string
    streamingMessages.set(qaId, "");
    
    // Clear thinking state after streaming has actually started and user can see content
    // Find the request ID for this qaId to complete the chat
    const requestId = qaIdToRequestId.get(qaId);
    if (requestId && currentChatSessionId) {
      // Wait a bit after streaming starts to ensure content is visible before clearing thinking state
      setTimeout(() => {
        chatManager.completeChat(currentChatSessionId, requestId);
        qaIdToRequestId.delete(qaId); // Clean up
      }, 500); // Delay to ensure first characters are visible before clearing thinking animation
    }
    
    let currentIndex = 0;
    const charsPerTick = 2 * STREAMING_SPEED_MULTIPLIER; // Characters to reveal per tick (scales with speed multiplier)
    const delayMs = Math.max(1, 2 / STREAMING_SPEED_MULTIPLIER); // Delay between updates (minimum 1ms, but browsers may limit to ~4ms)
    
    // Set the interval in the map FIRST so streamingIntervals.has(qaId) returns true immediately
    // This ensures getDisplayedText knows streaming is active
    const interval = setInterval(() => {
      if (currentIndex >= fullText.length) {
        // Streaming complete
        clearInterval(interval);
        streamingIntervals.delete(qaId);
        streamingComplete.add(qaId);
        streamingMessages.set(qaId, fullText); // Ensure full text is set
        messagesAlreadyShown.add(qaId); // Mark as already shown
        newlyAddedAnswers.delete(qaId); // Clean up
        // Trigger final update
        const currentMessages = $messages;
        if (currentMessages) {
          setMessages([...currentMessages]);
        }
        return;
      }
      
      // Reveal next chunk of characters
      currentIndex = Math.min(currentIndex + charsPerTick, fullText.length);
      const displayedText = fullText.substring(0, currentIndex);
      streamingMessages.set(qaId, displayedText);
      
      // Trigger reactivity by creating a new array reference
      const currentMessages = $messages;
      if (currentMessages) {
        setMessages([...currentMessages]);
      }
    }, delayMs);
    
    // Set interval in map immediately so streamingIntervals.has(qaId) is true
    streamingIntervals.set(qaId, interval);
    
    // Trigger immediate update to show empty string (streaming will start on next tick)
    const currentMessages = $messages;
    if (currentMessages) {
      setMessages([...currentMessages]);
    }
  }
  
  // Get displayed text for a message (streamed if streaming, full if complete)
  function getDisplayedText(message: any): string {
    if (message.role !== "assistant" || !message.chat_qa_id) {
      return message.content;
    }
    
    const qaId = message.chat_qa_id;
    
    // If currently streaming, use streamed text (check this FIRST)
    if (streamingIntervals.has(qaId) && streamingMessages.has(qaId)) {
      return streamingMessages.get(qaId) || "";
    }
    
    // If streaming is complete, use full content
    if (streamingComplete.has(qaId)) {
      return message.content;
    }
    
    // If message was already shown before (history/redisplay), show full text immediately
    if (messagesAlreadyShown.has(qaId)) {
      return message.content;
    }
    
    // If it's a newly added answer that should stream, return empty string initially
    // (streaming will start and update this)
    if (newlyAddedAnswers.has(qaId)) {
      return streamingMessages.get(qaId) || "";
    }
    
    // Otherwise, return full content (fallback)
    return message.content;
  }

  function markdownToHtml(text: string) {
    const html = marked.parse(text) as string;

    // Make external links open in new tabs
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const links = doc.querySelectorAll('a[href^="http"]');

    links.forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });

    return doc.body.innerHTML;
  }

  // isGenerating is now managed reactively by chatManager

  // SIMPLIFIED: Removed massive reactive block - messages are now synced explicitly via syncChats()
  // Only restore session on component remount if we have a persisted session
  // BUT: Don't sync if we're loading a session (loadSessionIntoChat will sync at the end)
  // CRITICAL: Also don't sync if currentChatSessionId was just set (might be a new session being set up)
  // We check this by verifying that currentChatId is truly empty, not just temporarily unset
  // For remounts, both would be set from persisted state, so currentChatId would match currentChatSessionId
  $: if (currentChatSessionId && (!currentChatId || currentChatId === "") && !isLoadingSession) {
    // Additional check: Only run if this looks like a remount scenario, not a new session setup
    // For remounts, we'd have messages already. For new sessions, messages are added after this point.
    const existingMessages = chatManager.getMessagesForChat(currentChatSessionId);
    
    // Only proceed if we have messages AND this seems like a remount (currentChatId hasn't been set yet)
    // For new sessions, currentChatId is set at the same time as currentChatSessionId, so this won't fire
    if (existingMessages.length > 0) {
      currentChatId = currentChatSessionId;
      loadChatContexts();
      syncChats(currentChatSessionId);
    }
    // Otherwise, this is likely a new session being set up - don't interfere
  }

  $: if ($chatState.isGenerating) {
    revealedText = loadingText;
  } else {
    revealedText = "";
  }

  // SIMPLIFIED: Removed competing reactive block - messages are now synced explicitly

  // Flag to prevent reactive context switching when loading a session
  let isLoadingSession: boolean = false;
  
  // Flag to prevent onMount from auto-matching after we've loaded a session
  let hasLoadedSession: boolean = false;
  
  // Load session when sessionToLoad is provided
  let lastLoadedSessionId: string | null = null;
  
  // Reference to the module-level Set for tracking manually loaded sessions
  // This persists across component remounts
  const loadedSessionIds = manuallyLoadedSessionIds;

  $: {
    if (sessionToLoad && sessionToLoad !== lastLoadedSessionId && object_id) {
      
      // Clear old session tracking if switching to a different session
      // BUT: Don't clear if we're loading the same session (can happen on remount)
      if (lastLoadedSessionId && lastLoadedSessionId !== sessionToLoad) {
        manuallyLoadedSessionIds.delete(lastLoadedSessionId);
        manuallyLoadedSessionChatIds.delete(lastLoadedSessionId);
        saveSessionChatMapping(); // Persist to localStorage
      }
      
      lastLoadedSessionId = sessionToLoad;
      manuallyLoadedSessionIds.add(sessionToLoad); // Track this session as manually loaded (module-level, persists across remounts)
      isLoadingSession = true; // Set flag to prevent reactive block from interfering
      hasLoadedSession = true; // Set flag to prevent onMount from auto-matching
      loadSessionIntoChat(sessionToLoad);
    }
  }

  async function loadSessionIntoChat(sessionId: string) {
    if (!object_id || !sessionId) return;

    try {
      const response = await api.post("/chat2GetSessionQA", {
        object_id: object_id,
        chat_session_id: sessionId,
      });
      
      // Check both possible response structures
      const responseData = response.data?.data || response.data;
      const qaRecords = responseData?.qa_records;
      
      const lastContextId = responseData?.last_context_key;
      const lastSent = responseData?.last_sent;
    
      if (qaRecords && Array.isArray(qaRecords) && qaRecords.length > 0) {
        
        // Convert Q&A records to messages (simple - just extract question and answer)
        const chatMessages: Array<{ role: "user" | "assistant"; content: string; timestamp: number }> = [];
        
        for (const qa of qaRecords) {
          // Get question text
          let questionText = "";
          if (typeof qa.request === "string") {
            questionText = qa.request;
          } else if (qa.request?.question) {
            questionText = qa.request.question;
          }
          
          // Get answer text
          let answerText = "";
          let extractedRoute: any = null; // Store route extracted from legacy JSON string
          if (typeof qa.response === "string") {
            answerText = qa.response;
          } else if (qa.response?.response) {
            const responseValue = qa.response.response;
            // Check if responseValue is a JSON string (legacy data from when JSON parsing failed)
            if (typeof responseValue === "string" && responseValue.trim().startsWith("{")) {
              try {
                const parsedResponse = JSON.parse(responseValue);
                // If it has an "answer" field, use that (this is the correct structure)
                if (parsedResponse.answer !== undefined) {
                  answerText = parsedResponse.answer;
                  // If route is empty/null/default and we found route data in the parsed response, use it as fallback
                  const hasValidRoute = qa.route && typeof qa.route === "object" && qa.route.path && qa.route.path !== "default";
                  if (!hasValidRoute && parsedResponse.route) {
                    extractedRoute = parsedResponse.route;
                    // Update qa.route so subsequent route processing logic will use it
                    qa.route = extractedRoute;
                  }
                } else {
                  // Not the expected structure, use the string as-is
                  answerText = responseValue;
                }
              } catch (parseError) {
                // Not valid JSON, use as-is
                answerText = responseValue;
              }
            } else {
              // Not a JSON string, use as-is
              answerText = responseValue;
            }
          } else if (qa.response?.message) {
            answerText = qa.response.message;
          } else if (qa.response?.content) {
            answerText = qa.response.content;
          } else if (qa.response && typeof qa.response === "object") {
            answerText = JSON.stringify(qa.response);
          }
          
          // Get timestamp from Q&A record
          const timestamp = qa.sent ? new Date(qa.sent).getTime() : Date.now();
          
          // Add user message (question)
          if (questionText) {
            chatMessages.push({
              role: "user",
              content: questionText,
              timestamp: timestamp,
              chat_qa_id: qa.chat_qa_id, // Include chat_qa_id from database
            });
          }
          
          // Add assistant message (answer)
          if (answerText) {
            const answerTimestamp = qa.response_time ? new Date(qa.response_time).getTime() : timestamp + 1000;
            chatMessages.push({
              role: "assistant",
              content: answerText,
              timestamp: answerTimestamp,
              chat_qa_id: qa.chat_qa_id, // Include chat_qa_id from database (same as question)
            });
          }
        }
        
        if (chatMessages.length > 0) {
          let customerToUse = customer_code;
          let contextToRestore: any = null;
          
          // CRITICAL: Use last_context_key from session if available (this is the context for the LAST question)
          // If not available, use the last Q&A record's chat_context_key as fallback
          // Otherwise fall back to the last Q&A record's request
          const lastQA = qaRecords[qaRecords.length - 1];
          const contextKeyToUse = lastContextId || lastQA?.chat_context_key;
         
          if (contextKeyToUse && customer_id) {
            try {
              const contextResponse = await api.post("/chat2GetChatContext", {
                chat_context_key: contextKeyToUse,
              });
              const context = contextResponse.data?.data?.context || contextResponse.data?.context;
              if (context) {
                // Validate context format - expect new format with documents array
                if (context.type === "documents") {
                  if (!context.documents || !Array.isArray(context.documents) || context.documents.length === 0) {
                    const errorMsg = `Invalid context format: documents array is missing, not an array, or empty. Context: ${JSON.stringify(context)}`;
                    logError(new Error(errorMsg), "Chatbot2", undefined);
                    throw new Error(errorMsg);
                  }
                  // Validate all documents have required fields
                  for (let i = 0; i < context.documents.length; i++) {
                    const doc = context.documents[i];
                    if (!doc || typeof doc !== "object") {
                      throw new Error(`Document at index ${i} in context is not an object`);
                    }
                    if (!doc.doc_key || typeof doc.doc_key !== "string" || doc.doc_key.trim() === "") {
                      throw new Error(`Document at index ${i} in context is missing required field 'doc_key'`);
                    }
                    if (!doc.doc_name || typeof doc.doc_name !== "string" || doc.doc_name.trim() === "") {
                      throw new Error(`Document at index ${i} in context is missing required field 'doc_name'`);
                    }
                    if (!doc.file_name || typeof doc.file_name !== "string" || doc.file_name.trim() === "") {
                      throw new Error(`Document at index ${i} in context is missing required field 'file_name'`);
                    }
                    if (!doc.project_id || typeof doc.project_id !== "string" || doc.project_id.trim() === "") {
                      throw new Error(`Document at index ${i} in context is missing required field 'project_id'`);
                    }
                  }
                }
                contextToRestore = context;

              } else {
                const errorMsg = "No context found for chat_context_key";
                logError(new Error(errorMsg), "Chatbot2", undefined);
                throw new Error(errorMsg);
              }
            } catch (error) {
              logError(error, "Chatbot2", undefined, { message: "Error loading context from chat_context_key" });
            }
          }
          
          // Fallback: Use last Q&A record's request if we don't have a context from chat_context_key
          if (!contextToRestore) {
            customerToUse = lastQA.request?.customer || customer_code;
            if (lastQA.request) {
              contextToRestore = lastQA.request;
            }
          } else {
            // Get customer from context or last Q&A record
            if (qaRecords.length > 0) {
              customerToUse = lastQA.request?.customer || customer_code;
            }
          }
          
          // CRITICAL: If we have customer code from request but no customer_id, look it up
          // This is needed when restoring from saved requests that only have customer code
          let customerIdToUse = customer_id;
          if (!customerIdToUse && customerToUse) {
            try {
              const customersData = await getMyCustomers();
              const matchingCustomer = customersData?.customers?.find(c => c.code === customerToUse);
              if (matchingCustomer) {
                customerIdToUse = matchingCustomer.customer_id;
              } else {
                logWarning(`loadSessionIntoChat - Could not find customer_id for customer code: ${customerToUse}`, "Chatbot2", undefined);
              }
            } catch (error) {
              logError(error, "Chatbot2", undefined, { message: "Error looking up customer_id from code" });
            }
          }
          
          // Restore context FIRST - this updates selectedObjects
          // Use customerIdToUse (which may have been looked up) instead of customer_id
          if (customerIdToUse && contextToRestore) {
            if (contextToRestore.type === "projects" || contextToRestore.type === "documents") {
              // This is a context object from chat_context table - use it directly
              await restoreContextFromContextObject(contextToRestore, customerIdToUse);
            } else {
              // This is a request object from Q&A record
              await restoreContextFromRequest(contextToRestore, customerIdToUse);
            }
          }
          
          // Wait a bit for selectedObjects to update
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Get what was ACTUALLY restored in selectedObjects to calculate correct chatId
          const selectedObjectsValue = get(selectedObjects);
          const actualDocumentsCount = selectedObjectsValue.documents?.length || 0;
          const actualProjectsCount = selectedObjectsValue.projects?.length || 0;
          
          // Determine selection type and IDs from what was actually restored
          let selectionType: "projects" | "documents" = "projects";
          let ids: string[] = [];
          
          if (actualProjectsCount > 0 && actualDocumentsCount === 0) {
            // Only projects were restored
            selectionType = "projects";
            ids = selectedObjectsValue.projects.map(p => p.project_id);
          } else if (actualDocumentsCount > 0) {
            // Documents were restored
            selectionType = "documents";
            // Get doc_keys from restored documents - need to match with allDocuments
            if (customerIdToUse && typeof customerIdToUse === "string") {
              const documentsData = await getDocumentsByCustomer(customerIdToUse);
              const allDocuments = documentsData?.documents || [];
              
              if (allDocuments.length > 0) {
                ids = selectedObjectsValue.documents.map(d => {
                  // Use doc_key - NO FALLBACKS
                  if (!d.doc_key || typeof d.doc_key !== "string" || d.doc_key.trim() === "") {
                    throw new Error(`Document in selectedObjects is missing required field 'doc_key': ${JSON.stringify(d)}`);
                  }
                  return d.doc_key.trim();
                }).filter((id: string) => id && id.length > 0);
              } else {
                // Use doc_key - NO FALLBACKS
                ids = selectedObjectsValue.documents.map(d => {
                  if (!d.doc_key || typeof d.doc_key !== "string" || d.doc_key.trim() === "") {
                    throw new Error(`Document in selectedObjects is missing required field 'doc_key': ${JSON.stringify(d)}`);
                  }
                  return d.doc_key.trim();
                }).filter((id: string) => id && id.length > 0);
              }
            } else {
              // Use doc_key - NO FALLBACKS
              ids = selectedObjectsValue.documents.map(d => {
                if (!d.doc_key || typeof d.doc_key !== "string" || d.doc_key.trim() === "") {
                  throw new Error(`Document in selectedObjects is missing required field 'doc_key': ${JSON.stringify(d)}`);
                }
                return d.doc_key.trim();
              }).filter((id: string) => id && id.length > 0);
            }
          } else {
            // Fallback: extract from last Q&A record's request if available
            if (qaRecords.length > 0) {
              const lastQA = qaRecords[qaRecords.length - 1];
            if (lastQA.request?.projects && Array.isArray(lastQA.request.projects) && lastQA.request.projects.length > 0) {
              selectionType = "projects";
              ids = lastQA.request.projects;
            } else if (lastQA.request?.documents && Array.isArray(lastQA.request.documents) && lastQA.request.documents.length > 0) {
              selectionType = "documents";
              ids = lastQA.request.documents.map((d: any) => {
                if (typeof d === "string") return d;
                return d?.doc_key || d?.file_name || d?.document_id || String(d);
              }).filter((id: string) => id && id.length > 0);
              }
            }
          }
          
          // Generate chatId from ACTUAL restored context
          const calculatedChatId = ids.length > 0 && customerToUse
            ? chatManager.generateChatId(customerToUse, selectionType, ids)
            : `session_${sessionId}`;
          
          
          // CRITICAL: Set session ID and mapping FIRST, before setting currentChatId
          // This ensures restoreIfNeeded() can find the mapping when it runs
          currentChatSessionId = sessionId;
          manuallyLoadedSessionChatIds.set(sessionId, calculatedChatId);
          saveSessionChatMapping(); // Persist to localStorage
          
          // Set state - set lastAppliedChatId FIRST to prevent reactive block from thinking context changed
          lastAppliedChatId = calculatedChatId;
          currentChatId = calculatedChatId;
          currentSelectionType = selectionType;
          currentIds = ids;
          
          // Store messages in chat store using sessionId (not calculatedChatId)
          // This ensures syncChats can find them
          ensureChatRecord(sessionId, { customer: customerToUse, selectionType, ids });
          replaceMessagesForChat(sessionId, chatMessages, { customer: customerToUse, selectionType, ids });
          
          // Update chat manager
          chatManager.setCurrentChat(sessionId, selectionType);
          
          // Store context for ALL Q&A records in this session
          // Each Q&A record has a chat_context_key that points to its context in the database
          // This ensures context bars can be shown for all questions when restoring
          // Map Q&A index to chat_id for easy lookup when matching messages
          const qaIndexToChatId = new Map<number, string>();
          let userMessageIndex = 0;
          for (const qa of qaRecords) {
            const qaContextKey = qa?.chat_context_key;
            
            if (qaContextKey) {
              try {
                // Load context from database using chat_context_key
                const contextResponse = await api.post("/chat2GetChatContext", {
                  chat_context_key: qaContextKey,
                });
                const context = contextResponse.data?.data?.context || contextResponse.data?.context;
                
                if (context && (context.type === "projects" || context.type === "documents")) {
                  const qaCustomer = qa?.request?.customer || customerToUse;
                  const qaSelectionType = context.type;
                  
                  if (qaCustomer && qaSelectionType) {
                    // Use chat_context_key directly from database instead of generating chat_id
                    // qaContextKey is the database ID that groups messages by context
                    
                    // Store context items for this Q&A
                    const contextItems: string[] = [];
                    if (qaSelectionType === "projects") {
                      if (!context.projectIds || !Array.isArray(context.projectIds) || context.projectIds.length === 0) {
                        throw new Error(`Invalid context format: projectIds array is missing, not an array, or empty`);
                      }
                      // Get project names
                      const projectsData = await getProjectsByCustomer(qaCustomer);
                      const allProjects = projectsData?.projects || [];
                      const projectMap = new Map(allProjects.map((p: Project) => [p.project_id, p]));
                      for (const projectId of context.projectIds) {
                        const project = projectMap.get(projectId);
                        const name = project?.name || project?.title || projectId;
                        if (name) contextItems.push(name);
                      }
                    } else {
                      // Get document names from documents array - NO FALLBACKS
                      if (!context.documents || !Array.isArray(context.documents) || context.documents.length === 0) {
                        throw new Error(`Invalid context format: documents array is missing, not an array, or empty`);
                      }
                      // Use doc_name from context documents
                      for (const doc of context.documents) {
                        if (!doc || typeof doc !== "object") {
                          throw new Error(`Document in context is not an object`);
                        }
                        if (!doc.doc_name || typeof doc.doc_name !== "string" || doc.doc_name.trim() === "") {
                          throw new Error(`Document in context is missing required field 'doc_name'`);
                        }
                        contextItems.push(doc.doc_name.trim());
                      }
                    }
                    
                    if (contextItems.length > 0) {
                      // Store context with chat_context_key (database ID) instead of generated chat_id
                      chatContexts.set(qaContextKey, { type: qaSelectionType, items: contextItems });
                      qaIndexToChatId.set(userMessageIndex, qaContextKey);
                    }
                  }
                }
              } catch (error) {
                const errorMsg = `Error loading context for Q&A ${userMessageIndex}, chat_context_key: ${qaContextKey}: ${error instanceof Error ? error.message : String(error)}`;
                logError(error, "Chatbot2", undefined, { message: errorMsg});
                throw new Error(errorMsg);
              }
            } else {
              const errorMsg = `Q&A record at index ${userMessageIndex} is missing chat_context_key`;
              logError(new Error(errorMsg), "Chatbot2", undefined);
              throw new Error(errorMsg);
            }
            userMessageIndex++;
          }
          
          // Store the Q&A index to chat_context_key mapping for this session
          // This maps Q&A index (user message index) to chat_context_key (database ID)
          // CRITICAL: Use sessionId (not calculatedChatId) so syncChats can find it
          qaIndexMappings.set(sessionId, qaIndexToChatId);
          saveChatContexts(); // Saves both chatContexts and qaIndexMappings
                   
          // Convert messages directly to UI format and assign chat_context_key from Q&A records
          loadMessageMetadata();
          // Create a map of chat_qa_id to qaRecord for quick lookup
          const qaRecordMap = new Map<string, any>();
          for (const qa of qaRecords) {
            if (qa.chat_qa_id) {
              qaRecordMap.set(qa.chat_qa_id, qa);
            }
          }
          
          const messagesToLoad = chatMessages.map((m, index) => {
            // chat_qa_id is required - every ChatMessage should have it
            const qaId = m.chat_qa_id;
            if (!qaId) {
              logError(new Error(`ChatMessage at index ${index} missing chat_qa_id`), "Chatbot2", undefined);
              return null;
            }
            let metadata = messageMetadata.get(qaId);
            
            // Find the Q&A record that matches this message's chat_qa_id
            // Both user and assistant messages with the same chat_qa_id should use the same qaRecord
            const qaRecord = qaRecordMap.get(qaId);
            
            // Match user messages to Q&A records to get the correct chat_context_key
            // Use chat_context_key from database (Q&A record) instead of generated chat_id
            let qaContextKey: string | undefined = undefined;
            
            if (qaRecord) {
              // Get chat_context_key directly from Q&A record (database ID)
              if (qaRecord.chat_context_key) {
                qaContextKey = qaRecord.chat_context_key;
              }
            }
            
            // Store timestamp from Q&A record if available and not already in metadata
            // Use chat_qa_id as the key for metadata storage
            if (qaRecord && qaRecord.sent && qaId) {
              const qaTimestamp = new Date(qaRecord.sent);
              const qaDuration = qaRecord.response_time ? Math.round(parseFloat(qaRecord.response_time)) : undefined;
              
              // Parse route if it's a string (PostgreSQL JSONB might return as string)
              let route = qaRecord.route || null;

              if (route && typeof route === 'string') {
                try {
                  route = JSON.parse(route);
                } catch (e) {
                logError(e, "Chatbot2", undefined, { message: `Failed to parse route JSON for qaId ${qaId}` });
                }
              }
              
              // Restore next_step_choice from route if it exists
              if (route && route.next_step_choice) {
                nextStepChoices.set(qaId, route.next_step_choice);
              }
              
              // Store metadata using chat_qa_id (database UUID) instead of msg_${id}_${index}
              if (!messageMetadata.has(qaId)) {
                messageMetadata.set(qaId, {
                  timestamp: qaTimestamp,
                  duration: qaDuration,
                  route: route,
                });

                // Update metadata reference
                metadata = messageMetadata.get(qaId);
              } else {
                // Update existing metadata with route if it's missing
                const existingMetadata = messageMetadata.get(qaId);
                if (existingMetadata && !existingMetadata.route && route) {
                  existingMetadata.route = route;
                  messageMetadata.set(qaId, existingMetadata);
                  metadata = messageMetadata.get(qaId);
                } else if (existingMetadata && existingMetadata.route && route) {
                  // Merge route data (preserve next_step_choice if it exists in loaded route)
                  existingMetadata.route = {
                    ...existingMetadata.route,
                    ...route,
                    // Preserve next_step_choice from loaded route if it exists
                    next_step_choice: route.next_step_choice || existingMetadata.route.next_step_choice
                  };
                  messageMetadata.set(qaId, existingMetadata);
                  metadata = messageMetadata.get(qaId);
                }
              }
            }
            
            // Convert timestamp to Date if it's a number or string
            let timestamp: Date | undefined = undefined;
            if (metadata?.timestamp) {
              const ts = metadata.timestamp as any;
              timestamp = ts instanceof Date ? ts : new Date(ts);
            } else if (m.timestamp) {
              const ts = m.timestamp as any;
              timestamp = ts instanceof Date ? ts : new Date(ts);
            } else if (qaRecord && qaRecord.sent) {
              // Fallback: use Q&A timestamp directly
              timestamp = new Date(qaRecord.sent);
            }
            
            return {
              id: qaId, // Use chat_qa_id as the Q&A ID
              chat_qa_id: qaId, // Include chat_qa_id in the UI Q&A object
              role: m.role,
              content: m.content,
              timestamp: timestamp,
              duration: metadata?.duration || (qaRecord?.response_time ? Math.round(parseFloat(qaRecord.response_time)) : undefined),
              chat_context_key: qaContextKey, // Track which chat_context_key this Q&A pair belongs to (database ID)
            };
          });
          
          // Save metadata to localStorage after storing Q&A timestamps
          saveMessageMetadata();
          
          // CRITICAL: Store messages in chatManager using sessionId (not calculatedChatId)
          // This ensures syncChats can find them
          replaceMessagesForChat(sessionId, chatMessages, { customer: customerToUse, selectionType, ids });
          chatManager.setCurrentChat(sessionId, selectionType);
          
          // Store Q&A index mapping using sessionId
          qaIndexMappings.set(sessionId, qaIndexToChatId);
          saveChatContexts(); // Saves both chatContexts and qaIndexMappings
          
          // Mark all messages as already shown (they're from history, so no streaming)
          messagesToLoad.forEach((msg) => {
            if (msg.chat_qa_id) {
              messagesAlreadyShown.add(msg.chat_qa_id);
              streamingComplete.add(msg.chat_qa_id);
              streamingMessages.set(msg.chat_qa_id, msg.content);
            }
          });
          
          // Sync messages to UI
          syncChats(sessionId);
          
          // Set persisted session ID (currentChatSessionId was already set above)
          setPersistedSessionId(sessionId);
          
          // Always reload sessions to ensure we have the latest data (including renamed sessions)
          // This ensures the session name is correct even if it was renamed after sessions were initially loaded
          await loadSessions();
          
          // Load session name and date from the freshly loaded sessions list
          const session = sessions.find(s => s.chat_session_id === sessionId);
          if (session) {
            currentSessionName = session.name || getNewChatName();
            // Use last_sent if available, otherwise fall back to started
            currentSessionStarted = lastSent || session.last_sent || session.started;
          } else {
            currentSessionName = getNewChatName();
            currentSessionStarted = lastSent || null;
          }
          
          // Scroll to bottom
          setTimeout(() => {
            if (messagesContainer) {
              messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
          }, 100);
          
          // Dispatch event that session is loaded
          dispatch("sessionLoaded");
          
          // Clear loading flag - messages are already set directly
          isLoadingSession = false;
          
          // Load associated chats for this session
          // Pass the sessionId to ensure it's excluded from the list
          loadAssociatedChats(sessionId);
        } else {
          isLoadingSession = false;
        }
      } else {
        isLoadingSession = false;
      }
    } catch (error) {
      logError("Error loading session into chat:", "Chatbot2", undefined, {error: error});
      isLoadingSession = false;
    }
  }

  async function restoreContextFromContextObject(context: any, customerIdToUse?: string) {
    const action = "restoreContextFromContextObject";
    // Use provided customerIdToUse or fall back to customer_id prop
    const effectiveCustomerId = customerIdToUse || customer_id;
    if (!context || !effectiveCustomerId) {
      return;
    }

    try {

      if (context.type === "projects" && context.projectIds && Array.isArray(context.projectIds)) {
        // Restore projects: get all documents for these projects and add them
        
        // Get all documents for the customer
        const documentsData = await getDocumentsByCustomer(effectiveCustomerId);
        const allDocuments = documentsData?.documents || [];
        
        // Get all documents for the specified projects
        const documentsToRestore: Document[] = [];
        const projectDocCounts: Record<string, number> = {};
        
        for (const projectId of context.projectIds) {
          const projectDocs = allDocuments.filter((doc: Document) => doc.project_id === projectId);
          projectDocCounts[projectId] = projectDocs.length;
          documentsToRestore.push(...projectDocs);
        }
        
        // Get projects to get project names
        const projectsData = await getProjectsByCustomer(effectiveCustomerId);
        const allProjects = projectsData?.projects || [];
        const projectMap = new Map(allProjects.map((p: Project) => [p.project_id, p]));
        
        // Clear current selection and add all documents
        selectedObjects.clear(); // Clear everything first
        for (const doc of documentsToRestore) {
          const project = projectMap.get(doc.project_id);
          // CRITICAL: doc_path is the storage file name (like "CBq-8ZgBW4DRgsiTpnQi.pdf")
          // Use doc_path from API response, or construct from doc_key if not available
          const storageFileName = getDocPathFromDocument(doc);
          // file_name/doc_filename is the user-friendly display name (like "Abe-2015.pdf")
          const displayFileName = getDocFilenameFromDocument(doc);
          selectedObjects.add({
            document_id: doc.document_id,
            file_name: storageFileName, // Blob storage name (was doc_path)
            doc_name: doc.doc_title || displayFileName, // User-friendly name (was doc_title)
            doc_key: doc.doc_key, // Unique identifier
            customer_code: doc.customer_code,
            project_id: doc.project_id,
            project_name: project?.name || doc.project || "Unknown Project",
            stage: doc.stage, // Document stage (Protocol, Screening, etc.)
            project_total_docs: projectDocCounts[doc.project_id] || 0,
          });
        }
        
        // Store context for display in UI
        const projectNames = context.projectIds
          .map((projectId: string) => {
            const project = projectMap.get(projectId);
            return project?.name || project?.title || projectId;
          })
          .filter((name: string) => name);
        // Calculate chat_id from context to store it
        // CRITICAL: Use customer_code not customer_id (UUID) for generateChatId
        if (customer_code && projectNames.length > 0) {
          const chat_id = chatManager.generateChatId(customer_code, "projects", context.projectIds);
          chatContexts.set(chat_id, { type: "projects", items: projectNames });
          saveChatContexts(); // Persist to localStorage
        }
      } else if (context.type === "documents" && context.documents && Array.isArray(context.documents)) {
        
        // Validate all documents have required fields - NO FALLBACKS
        for (let i = 0; i < context.documents.length; i++) {
          const doc = context.documents[i];
          if (!doc || typeof doc !== "object") {
            throw new Error(`Document at index ${i} in context is not an object`);
          }
          if (!doc.doc_key || typeof doc.doc_key !== "string" || doc.doc_key.trim() === "") {
            throw new Error(`Document at index ${i} in context is missing required field 'doc_key'`);
          }
          if (!doc.doc_name || typeof doc.doc_name !== "string" || doc.doc_name.trim() === "") {
            throw new Error(`Document at index ${i} in context is missing required field 'doc_name'`);
          }
          if (!doc.file_name || typeof doc.file_name !== "string" || doc.file_name.trim() === "") {
            throw new Error(`Document at index ${i} in context is missing required field 'file_name'`);
          }
          if (!doc.project_id || typeof doc.project_id !== "string" || doc.project_id.trim() === "") {
            throw new Error(`Document at index ${i} in context is missing required field 'project_id'`);
          }
        }
        
        // Deduplicate by doc_key
        const seenDocKeys = new Set<string>();
        const uniqueDocuments: Array<{ doc_key: string; doc_name: string; file_name: string; project_id: string }> = [];
        for (const doc of context.documents) {
          const doc_key = doc.doc_key.trim();
          if (seenDocKeys.has(doc_key)) {
            logWarning(`Skipping duplicate doc_key in context: ${doc_key}`, "Chatbot2", undefined);
            continue;
          }
          seenDocKeys.add(doc_key);
          uniqueDocuments.push({
            doc_key: doc_key,
            doc_name: doc.doc_name.trim(),
            file_name: doc.file_name.trim(),
            project_id: doc.project_id.trim(),
          });
        }      
       
        // Get projects to get project names
        const projectsData = await getProjectsByCustomer(effectiveCustomerId);
        const allProjects = projectsData?.projects || [];
        const projectMap = new Map(allProjects.map((p: Project) => [p.project_id, p]));
        
        // Clear current selection and add documents
        selectedObjects.clear();
        for (const doc of uniqueDocuments) {
          const project = projectMap.get(doc.project_id);
          const docToAdd = {
            document_id: "", // Not available from context
            file_name: doc.file_name, // Blob storage name (derived name)
            doc_name: doc.doc_name, // User-friendly name
            doc_key: doc.doc_key, // Unique identifier
            customer_code: customer_code, // CRITICAL: Use customer_code not customer_id (UUID)
            project_id: doc.project_id,
            project_name: project?.name || "Unknown Project",
            stage: doc.stage, // Document stage (Protocol, Screening, etc.)
          };
          selectedObjects.add(docToAdd);
        }
        
        const finalSelected = get(selectedObjects);
        
        // Store context for display in UI - use doc_name from context documents
        // CRITICAL: Use customer_code not customer_id (UUID) for generateChatId
        const documentNames = uniqueDocuments.map(d => d.doc_name);
        if (customer_code && documentNames.length > 0) {
          const ids = uniqueDocuments.map(d => d.doc_key);
          const chat_id = chatManager.generateChatId(customer_code, "documents", ids);
          chatContexts.set(chat_id, { type: "documents", items: documentNames });
          saveChatContexts(); // Persist to localStorage
        }
      } else {
        const errorMsg = `Unknown context type or invalid format: type="${context.type}", has documents array=${!!context.documents}, has documentIds=${!!context.documentIds}`;
        logError(new Error(errorMsg), "Chatbot2", undefined);
        throw new Error(errorMsg);
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error restoring context from context object" });
    }
  }

  async function restoreContextFromItems(contextType: 'documents' | 'projects', itemNames: string[]) {
    if (!customer_id || !itemNames || itemNames.length === 0) {
      return;
    }

    try {
      if (contextType === 'documents') {
        // Get all documents for the customer
        const documentsData = await getDocumentsByCustomer(customer_id);
        const allDocuments = documentsData?.documents || [];

        // Match documents by doc_name
        const documentsToRestore: Document[] = [];
        for (const itemName of itemNames) {
          const matchedDoc = allDocuments.find((doc: Document) => {
            const docName = doc.doc_name || doc.doc_title || (doc as any).doc_filename || doc.file_name;
            return docName && docName.trim() === itemName.trim();
          });
          if (matchedDoc) {
            documentsToRestore.push(matchedDoc);
          } else {
            logWarning(`Could not find document with name: ${itemName}`, "Chatbot2", undefined);
          }
        }

        if (documentsToRestore.length === 0) {
          logWarning(`No documents matched for restoration`, "Chatbot2", undefined);
          return;
        }

        // Get projects to get project names
        const projectsData = await getProjectsByCustomer(customer_id);
        const allProjects = projectsData?.projects || [];
        const projectMap = new Map(allProjects.map((p: Project) => [p.project_id, p]));

        // Get actual total document counts per project (not just the ones we're restoring)
        const projectDocCounts: Record<string, number> = {};
        const projectIds = new Set(documentsToRestore.map(doc => doc.project_id));
        for (const projectId of projectIds) {
          const projectDocs = allDocuments.filter((doc: Document) => doc.project_id === projectId);
          projectDocCounts[projectId] = projectDocs.length;
        }

        // Clear current selection and add documents
        selectedObjects.clear();
        for (const doc of documentsToRestore) {
          const project = projectMap.get(doc.project_id);
          const storageFileName = getDocPathFromDocument(doc);
          const displayFileName = getDocFilenameFromDocument(doc);
          selectedObjects.add({
            document_id: doc.document_id || "",
            file_name: storageFileName,
            doc_name: doc.doc_name || doc.doc_title || displayFileName,
            doc_key: doc.doc_key,
            customer_code: doc.customer_code || customer_code,
            project_id: doc.project_id,
            project_name: project?.name || doc.project || "Unknown Project",
            stage: doc.stage, // Document stage (Protocol, Screening, etc.)
            project_total_docs: projectDocCounts[doc.project_id] || 0,
          });
        }

      } else if (contextType === 'projects') {
        // Get all projects for the customer
        const projectsData = await getProjectsByCustomer(customer_id);
        const allProjects = projectsData?.projects || [];

        // Match projects by name
        const projectsToRestore: Project[] = [];
        for (const itemName of itemNames) {
          const matchedProject = allProjects.find((p: Project) => {
            const projectName = p.name || p.title || p.project_id;
            return projectName && projectName.trim() === itemName.trim();
          });
          if (matchedProject) {
            projectsToRestore.push(matchedProject);
          } else {
            logWarning(`Could not find project with name: ${itemName}`, "Chatbot2", undefined);
          }
        }

        if (projectsToRestore.length === 0) {
          return;
        }

        // Get all documents for the customer
        const documentsData = await getDocumentsByCustomer(customer_id);
        const allDocuments = documentsData?.documents || [];

        // Get all documents for the specified projects
        const documentsToRestore: Document[] = [];
        const projectDocCounts: Record<string, number> = {};

        for (const project of projectsToRestore) {
          const projectDocs = allDocuments.filter((doc: Document) => doc.project_id === project.project_id);
          projectDocCounts[project.project_id] = projectDocs.length;
          documentsToRestore.push(...projectDocs);
        }

        // Clear current selection and add all documents
        selectedObjects.clear();
        for (const doc of documentsToRestore) {
          const project = projectsToRestore.find(p => p.project_id === doc.project_id);
          const storageFileName = getDocPathFromDocument(doc);
          const displayFileName = getDocFilenameFromDocument(doc);
          selectedObjects.add({
            document_id: doc.document_id || "",
            file_name: storageFileName,
            doc_name: doc.doc_name || doc.doc_title || displayFileName,
            doc_key: doc.doc_key,
            customer_code: doc.customer_code || customer_code,
            project_id: doc.project_id,
            project_name: project?.name || "Unknown Project",
            stage: doc.stage, // Document stage (Protocol, Screening, etc.)
            project_total_docs: projectDocCounts[doc.project_id] || 0,
          });
        }
      }
    } catch (error) {
      logError("Error restoring context from items:", "Chatbot2", undefined, {error: error});
    }
  }

  async function restoreContextFromRequest(request: any, customerIdToUse?: string) {
    // Use provided customerIdToUse or fall back to customer_id prop
    const effectiveCustomerId = customerIdToUse || customer_id;
    if (!request || !effectiveCustomerId) {
      return;
    }

    try {
      // Extract projects or documents from the request
      // The request field contains: { question, projects?, documents?, customer, stage, ... }
      const requestProjects = request.projects;
      const requestDocuments = request.documents;

      if (requestProjects && Array.isArray(requestProjects) && requestProjects.length > 0) {
        
        // Get all documents for the customer
        const documentsData = await getDocumentsByCustomer(effectiveCustomerId);
        const allDocuments = documentsData?.documents || [];
        
        // Get all documents for the specified projects
        const documentsToRestore: Document[] = [];
        const projectDocCounts: Record<string, number> = {};
        
        for (const projectId of requestProjects) {
          const projectDocs = allDocuments.filter((doc: Document) => doc.project_id === projectId);
          projectDocCounts[projectId] = projectDocs.length;
          documentsToRestore.push(...projectDocs);
        }
        
        // Get projects to get project names
        const projectsData = await getProjectsByCustomer(effectiveCustomerId);
        const allProjects = projectsData?.projects || [];
        const projectMap = new Map(allProjects.map((p: Project) => [p.project_id, p]));
        
        // Clear current selection and add all documents
        selectedObjects.clear(); // Clear everything first
        for (const doc of documentsToRestore) {
          const project = projectMap.get(doc.project_id);
          // CRITICAL: doc_path is the storage file name (like "CBq-8ZgBW4DRgsiTpnQi.pdf")
          // Use doc_path from API response, or construct from doc_key if not available
          const storageFileName = getDocPathFromDocument(doc);
          // file_name/doc_filename is the user-friendly display name (like "Abe-2015.pdf")
          const displayFileName = getDocFilenameFromDocument(doc);
          selectedObjects.add({
            document_id: doc.document_id,
            file_name: storageFileName, // Blob storage name (was doc_path)
            doc_name: doc.doc_title || displayFileName, // User-friendly name (was doc_title)
            doc_key: doc.doc_key, // Unique identifier
            customer_code: doc.customer_code,
            project_id: doc.project_id,
            project_name: project?.name || doc.project || "Unknown Project",
            stage: doc.stage, // Document stage (Protocol, Screening, etc.)
            project_total_docs: projectDocCounts[doc.project_id] || 0,
          });
        }
        
      } else if (requestDocuments && Array.isArray(requestDocuments) && requestDocuments.length > 0) {
       
        // Get all documents for the customer
        const documentsData = await getDocumentsByCustomer(effectiveCustomerId);
        const allDocuments = documentsData?.documents || [];
        
        // Find documents matching the doc_keys from the request
        // requestDocuments can be an array of doc_key strings or objects with doc_key, document_id, or file_name
        // Use a Set to track unique documents by doc_key to avoid duplicates
        const documentsToRestore: Document[] = [];
        const seenDocKeys = new Set<string>();
        const unmatchedItems: any[] = [];
        
        for (const docItem of requestDocuments) {
          let doc: Document | undefined;
          
          if (typeof docItem === "string") {
            // String: try to match by doc_key first, then file_name
            doc = allDocuments.find((d: Document) => d.doc_key === docItem);
            if (!doc) {
              doc = allDocuments.find((d: Document) => d.file_name === docItem);
            }
            if (!doc) {
              // Try matching by document_id if the string looks like a UUID
              const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
              if (uuidPattern.test(docItem)) {
                doc = allDocuments.find((d: Document) => d.document_id === docItem);
              }
            }
          } else if (docItem && typeof docItem === "object") {
            // Object: try multiple matching strategies
            if (docItem.document_id) {
              doc = allDocuments.find((d: Document) => d.document_id === docItem.document_id);
            }
            if (!doc && docItem.doc_key) {
              doc = allDocuments.find((d: Document) => d.doc_key === docItem.doc_key);
            }
            if (!doc && docItem.file_name) {
              doc = allDocuments.find((d: Document) => d.file_name === docItem.file_name);
            }
            // Also try matching by any string property that might be a doc_key
            if (!doc) {
              for (const [key, value] of Object.entries(docItem)) {
                if (typeof value === "string" && value.length > 0) {
                  doc = allDocuments.find((d: Document) => d.doc_key === value || d.file_name === value);
                  if (doc) break;
                }
              }
            }
          }
          
          if (doc) {
            // Only add if we haven't seen this doc_key before
            if (!seenDocKeys.has(doc.doc_key)) {
              documentsToRestore.push(doc);
              seenDocKeys.add(doc.doc_key);
            }
          } else {
            unmatchedItems.push(docItem);
          }
        }
        
        if (unmatchedItems.length > 0) {
          logWarning(`Unmatched document items: ${unmatchedItems}`, "Chatbot2", undefined);
        }
        
        // Get projects to get project names
        const projectsData = await getProjectsByCustomer(effectiveCustomerId);
        const allProjects = projectsData?.projects || [];
        const projectMap = new Map(allProjects.map((p: Project) => [p.project_id, p]));
        
        // Clear current selection and add documents
        selectedObjects.clear(); // Use clear() instead of clearDocuments()
        for (const doc of documentsToRestore) {
          const project = projectMap.get(doc.project_id);
          // CRITICAL: doc_path is the storage file name (like "CBq-8ZgBW4DRgsiTpnQi.pdf")
          // Use doc_path from API response, or construct from doc_key if not available
          const storageFileName = getDocPathFromDocument(doc);
          // file_name/doc_filename is the user-friendly display name (like "Abe-2015.pdf")
          const displayFileName = getDocFilenameFromDocument(doc);
          // Don't provide project_total_docs to prevent auto-adding projects
          // We want documents context, not projects context
          selectedObjects.add({
            document_id: doc.document_id,
            file_name: storageFileName, // Blob storage name (was doc_path)
            doc_name: doc.doc_title || displayFileName, // User-friendly name (was doc_title)
            doc_key: doc.doc_key, // Unique identifier
            customer_code: doc.customer_code,
            project_id: doc.project_id,
            project_name: project?.name || doc.project || "Unknown Project",
            stage: doc.stage, // Document stage (Protocol, Screening, etc.)
            // Explicitly don't provide project_total_docs to prevent auto-project selection
          });
        }
        
        // After adding documents, explicitly clear projects to ensure documents context is shown
        // The store might auto-add projects if all documents in a project are selected
        const selectedObjectsValue = get(selectedObjects);
        if (selectedObjectsValue.projects.length > 0) {
          selectedObjects.update(state => ({
            ...state,
            projects: [] // Clear projects to show documents context
          }));
        }
        
        const finalSelectedObjectsValue = get(selectedObjects);
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error restoring context from request" });
    }
  }

  async function restoreContextFromKey(chatContextKey: string) {
    if (!chatContextKey || !customer_id) {
       return;
    }

    try {
      const contextResponse = await api.post("/chat2GetChatContext", {
        chat_context_key: chatContextKey,
      });
      
      const context = contextResponse.data?.data?.context || contextResponse.data?.context;
      if (!context) {
        return;
      }

      // Use the shared restore function
      await restoreContextFromContextObject(context);
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error restoring context from key" });
    }
  }

  onMount(() => {
    // CRITICAL: Restore session->chat mapping from localStorage FIRST
    // This must happen before any reactive statements run
    loadSessionChatMapping();
    
    // CRITICAL: Restore currentChatSessionId from persisted value
    // This must happen before any reactive statements run
    const persistedId = getPersistedSessionId();
    if (persistedId && !currentChatSessionId) {
      currentChatSessionId = persistedId;
    }
    
    // Load persisted message metadata
    loadMessageMetadata();
        
    // If we have a loaded session but the header is not set (component remounted),
    // try to restore it by matching the current chat_id to a loaded session
    if (!currentChatSessionId && manuallyLoadedSessionChatIds.size > 0 && currentChatId) {
      // Find which session matches the current chat_id
      for (const [sessionId, chatId] of manuallyLoadedSessionChatIds.entries()) {
        if (chatId === currentChatId) {
          // Restore the session ID - we'll load the session name below
          currentChatSessionId = sessionId;
          setPersistedSessionId(sessionId);
          lastLoadedSessionId = sessionId;
          break;
        }
      }
    }

    // Load sessions list, then try to match current messages to a session
    // Skip this if we're loading a specific session (sessionToLoad is set) or if we've already loaded a session
    // Check loadedSessionIds Set which persists across remounts (module-level)
    const shouldSkipMatching = sessionToLoad || hasLoadedSession || lastLoadedSessionId || manuallyLoadedSessionIds.size > 0;
    
    if (!shouldSkipMatching) {
      loadSessions().then(() => {
        // FIRST: Check if we have a currentChatId that maps to a manually loaded session
        // This handles remounts where state was reset but the chatId mapping still exists
        // CRITICAL: Check the mapping even if maps appear empty (they might have been cleared)
        // We'll try to restore from the sessions list by matching chatId patterns
        if (currentChatId && currentChatId !== "") {
          // Try to find a session that matches this chatId
          // First check the mapping if it exists
          let matchingSessionId: string | undefined;
          if (manuallyLoadedSessionChatIds.size > 0) {
            matchingSessionId = Array.from(manuallyLoadedSessionChatIds.entries()).find(([sessionId, chatId]) => chatId === currentChatId)?.[0];
          }
          
          // If no mapping found, try to find by matching chat context in sessions
          // This is a fallback when maps are cleared but we still have the chatId
          if (!matchingSessionId && sessions.length > 0) {
            // For now, we'll rely on the later code to handle this
            // But we can try to restore if we have messages for this chatId
            const messages = chatManager.getMessagesForChat(currentChatId);
          }
          
          if (matchingSessionId) {
            // Restore session name from sessions list FIRST (most reliable source)
            const session = sessions.find(s => s.chat_session_id === matchingSessionId);
            if (session) {
              currentChatSessionId = matchingSessionId;
              setPersistedSessionId(matchingSessionId);
              currentSessionName = session.name || getDefaultNewChatName();
                    currentSessionStarted = session.last_sent || session.started;
              // Re-store in module-level maps for future remounts
              manuallyLoadedSessionIds.add(matchingSessionId);
              manuallyLoadedSessionChatIds.set(matchingSessionId, currentChatId);
              saveSessionChatMapping(); // Persist to localStorage
              return; // Exit early, don't try to auto-match
            }
          }
        }
        
        // SECOND: If we restored a session header on mount, load its name now
        // BUT: Don't overwrite if we're loading a specific session
        if (currentChatSessionId && !currentSessionName && !isLoadingSession && !sessionToLoad) {
          const session = sessions.find(s => s.chat_session_id === currentChatSessionId);
          if (session) {
            currentSessionName = session.name || getDefaultNewChatName();
            currentSessionStarted = session.last_sent || session.started;
          }
        }
        
        // CRITICAL: Skip auto-matching if we're loading a session or if one was just loaded
        // This prevents onMount from overwriting a session that's being loaded
        if (sessionToLoad || isLoadingSession || hasLoadedSession || lastLoadedSessionId) {
          return;
        }
        
        // Check if any of the sessions in the list have been manually loaded
        // If so, skip auto-matching entirely to prevent overwriting
        if (manuallyLoadedSessionIds.size > 0) {
          const anySessionWasLoaded = sessions.some(s => s.chat_session_id && manuallyLoadedSessionIds.has(s.chat_session_id));
          if (anySessionWasLoaded) {
            const loadedSessions = sessions.filter(s => s.chat_session_id && manuallyLoadedSessionIds.has(s.chat_session_id));
            return;
          }
        }
        
        // If we have messages but no session ID, try to find the matching session
        // Only do this if we haven't manually loaded a session
        // IMPORTANT: Check all flags here too, in case currentChatSessionId was reset
        if (!currentChatSessionId && currentChatId && currentChatId !== "") {
          const messages = chatManager.getMessagesForChat(currentChatId);
          if (messages.length > 0) {
            
            // CRITICAL: If we have messages but the mapping is empty, this likely means:
            // 1. A session was manually loaded but component remounted and maps were cleared
            // 2. We should NOT auto-match in this case - the persisted value should restore it
            // Only auto-match if we're absolutely sure no session was manually loaded
            if (manuallyLoadedSessionChatIds.size === 0 && manuallyLoadedSessionIds.size === 0) {
              // Maps are empty - this could mean:
              // - Component remounted and maps were cleared (but persisted value should restore)
              // - No session was ever manually loaded (safe to auto-match)
              // Check if persisted value exists - if so, it should have been restored already
              // If persisted value doesn't exist AND no loading flags are set, safe to auto-match
              const persistedId = getPersistedSessionId();
              if (!persistedId && !isLoadingSession && !sessionToLoad && !hasLoadedSession && !lastLoadedSessionId) {
                // No persisted value and no loading flags - safe to auto-match to most recent
                if (sessions.length > 0) {
                  const mostRecent = sessions[0];
                  if (mostRecent.chat_session_id) {
                    currentChatSessionId = mostRecent.chat_session_id;
                    setPersistedSessionId(mostRecent.chat_session_id);
                    currentSessionName = mostRecent.name || getDefaultNewChatName();
                    currentSessionStarted = mostRecent.last_sent || mostRecent.started;
                   loadSessionQA(mostRecent.chat_session_id);
                  }
                }
              }
            } else {
              // Manual loading happened - check if currentChatId maps to a manually loaded session
              const matchingSessionId = Array.from(manuallyLoadedSessionChatIds.entries()).find(([sessionId, chatId]) => chatId === currentChatId)?.[0];
              if (matchingSessionId) {
                // Restore session from sessions list
                const session = sessions.find(s => s.chat_session_id === matchingSessionId);
                if (session) {
                  currentChatSessionId = matchingSessionId;
                  setPersistedSessionId(matchingSessionId);
                  currentSessionName = session.name || getDefaultNewChatName();
                  currentSessionStarted = session.last_sent || session.started;
                  manuallyLoadedSessionIds.add(matchingSessionId);
                  return; // Exit early, don't try to auto-match
                }
              }
            }
          }
        }
      });
    }
    scrollToBottom();
    // Position loading is handled by reactive statements above
  });

  onDestroy(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    // Save immediately on destroy if there's a pending save
    if (pendingSave) {
      doSaveSplitter();
    }
    
    // Clean up all streaming intervals
    streamingIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    streamingIntervals.clear();
    streamingMessages.clear();
    streamingComplete.clear();
  });

  // Per-character ripple effect for loading text
  function getRippleText(text: string) {
    return text.split("").map((char, i) => ({
      char,
      style: `animation-delay: ${i * 0.08}s;`,
    }));
  }

  // Helpers: selection normalization and chat_id lookup
  function normalizeProjects(input: any[]): string[] {
    if (!Array.isArray(input)) return [];
    return input
      .map((p) => (typeof p === "string" ? p : p?.project_id))
      .filter((id): id is string => typeof id === "string" && id.length > 0)
      .sort();
  }

  function normalizeDocuments(
    input: any[],
  ): { doc_key: string; doc_name: string; file_name: string; project_id: string }[] {
    if (!Array.isArray(input)) return [];
    return input
      .map((d) => ({
        doc_key: d?.doc_key,
        doc_name: d?.doc_name,
        file_name: d?.file_name,
        project_id: d?.project_id,
      }))
      .filter(
        (x) =>
          typeof x.doc_key === "string" &&
          x.doc_key.length > 0 &&
          typeof x.doc_name === "string" &&
          x.doc_name.length > 0 &&
          typeof x.file_name === "string" &&
          x.file_name.length > 0 &&
          typeof x.project_id === "string" &&
          x.project_id.length > 0,
      )
      .sort((a, b) => a.doc_key.localeCompare(b.doc_key));
  }

  async function copyToClipboard(messageIndex: number) {
    try {
      // Use the reactive messages array
      const messagesToUse = currentMessagesForCopy;
      if (!messagesToUse || messageIndex < 0 || messageIndex >= messagesToUse.length) {
        logError(new Error("Invalid message index for copy"), "Chatbot2", undefined);
        return;
      }
      
      const assistantMessage = messagesToUse[messageIndex];
      if (assistantMessage.role !== "assistant") {
        logError(new Error("Copy called on non-assistant message"), "Chatbot2", undefined);
        return;
      }
      
      // Find the previous user message (the question)
      let question = "";
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messagesToUse[i].role === "user") {
          question = messagesToUse[i].content;
          break;
        }
      }
      
      // Strip HTML from assistant message content (it's markdown rendered as HTML)
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = assistantMessage.content;
      const answer = tempDiv.textContent || tempDiv.innerText || assistantMessage.content;
      
      // Combine question and answer
      const textToCopy = question ? `Q: ${question}\n\nA: ${answer}` : answer;
      
      await navigator.clipboard.writeText(textToCopy);
      
      // Show toast notification - will be positioned relative to the button
      showCopyToast = messageIndex;
      setTimeout(() => {
        showCopyToast = null;
        copyToastButtonRef = null;
      }, 2000); // Hide after 2 seconds
    } catch (err) {
      logError(err, "Chatbot2", undefined, { message: "Failed to copy text" });
    }
  }

  async function copyQuestionToClipboard(messageIndex: number) {
    try {
      // Use the reactive messages array
      const messagesToUse = currentMessagesForCopy;
      if (!messagesToUse || messageIndex < 0 || messageIndex >= messagesToUse.length) {
        logError(new Error("Invalid message index for copy"), "Chatbot2", undefined);
        return;
      }
      
      const userMessage = messagesToUse[messageIndex];
      if (userMessage.role !== "user") {
        logError(new Error("Copy called on non-user message"), "Chatbot2", undefined);
        return;
      }
      
      // Get the question text (strip HTML if present)
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = userMessage.content;
      const questionText = tempDiv.textContent || tempDiv.innerText || userMessage.content;
      
      await navigator.clipboard.writeText(questionText);
      
      // Show toast notification - will be positioned relative to the button
      showQuestionCopyToast = messageIndex;
      setTimeout(() => {
        showQuestionCopyToast = null;
        questionCopyToastButtonRef = null;
      }, 2000); // Hide after 2 seconds
    } catch (err) {
      logError(err, "Chatbot2", undefined, { message: "Failed to copy question text" });
    }
  }

  function resubmitQuestion(messageIndex: number) {
    try {
      // Use the reactive messages array
      const messagesToUse = currentMessagesForCopy;
      if (!messagesToUse || messageIndex < 0 || messageIndex >= messagesToUse.length) {
        logError(new Error("Invalid message index for resubmit"), "Chatbot2", undefined);
        return;
      }
      
      const userMessage = messagesToUse[messageIndex];
      if (userMessage.role !== "user") {
        logError(new Error("Resubmit called on non-user message"), "Chatbot2", undefined);
        return;
      }
      
      // Get the question text (strip HTML if present)
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = userMessage.content;
      const questionText = tempDiv.textContent || tempDiv.innerText || userMessage.content;
      
      if (!questionText) {
        logError(new Error("Could not find question to resubmit"), "Chatbot2", undefined);
        return;
      }
      
      // Set the input to the question text and submit
      $input = questionText;
      handleSubmitWithLogging(new Event("click"), false);
    } catch (err) {
      logError(err, "Chatbot2", undefined, { message: "Failed to resubmit question" });
    }
  }

  // Session management functions
  async function loadSessions() {
    if (!object_id) {
      return;
    }
    
    isLoadingSessions = true;
    try {
      const response = await api.post("/chat2GetSessions", {
        object_id: object_id,
      });
      if (response.data?.sessions) {
        sessions = response.data.sessions;
      }
      
      // Reload associated chats when sessions are loaded (in case a new chat was added)
      if (currentChatSessionId) {
        loadAssociatedChats();
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error loading sessions" });
    } finally {
      isLoadingSessions = false;
    }
  }

  // Get the current chat's context key from the session, messages, or current selection
  function getCurrentChatContextKey(): string | null {
    // First, try to get it from the current session
    if (currentChatSessionId) {
      const currentSession = sessions.find(s => s.chat_session_id === currentChatSessionId);
      if (currentSession?.last_context_key) {
        return currentSession.last_context_key;
      }
    }
    
    // Fallback: get it from the last message's chat_context_key
    if ($messages && $messages.length > 0) {
      const lastMessage = $messages[$messages.length - 1];
      if (lastMessage?.chat_context_key) {
        return lastMessage.chat_context_key;
      }
    }
    
    // Final fallback: compute it from the current selection (projects or documents)
    // This allows associated chats to update when context changes, even before a question is submitted
    if (derivedProjects.length > 0 || derivedDocuments.length > 0) {
      try {
        // Determine selection type: projects take precedence
        const selectionType = derivedProjects.length > 0 ? "projects" : "documents";
        const ids: string[] = [];
        
        if (selectionType === "projects") {
          // Extract project IDs
          for (const project of derivedProjects) {
            if (project?.project_id) {
              ids.push(project.project_id.trim());
            }
          }
        } else {
          // Extract document doc_keys
          for (const doc of derivedDocuments) {
            if (doc?.doc_key) {
              ids.push(doc.doc_key.trim());
            }
          }
        }
        
        if (ids.length > 0) {
          // Generate deterministic UUID from document/project IDs using UUID v5
          // Must match backend logic exactly
          const sortedIds = [...ids].sort();
          const hashInput = selectionType === "projects" 
            ? `projects_${sortedIds.join("_")}`
            : `documents_${sortedIds.join("_")}`;
          
          // Use UUID v5 with same namespaces as backend
          const NAMESPACE_DOCUMENTS = "6ba7b810-9dad-11d1-80b4-00c04fd430c8";
          const NAMESPACE_PROJECTS = "6ba7b811-9dad-11d1-80b4-00c04fd430c8";
          
          const chat_context_key = uuidv5(hashInput, selectionType === "projects" ? NAMESPACE_PROJECTS : NAMESPACE_DOCUMENTS);
          return chat_context_key;
        }
      } catch (error) {
        logError(error, "Chatbot2", undefined, { message: "Error computing context key from selection" });
      }
    }
    
    return null;
  }

  // Track the last session ID we loaded associated chats for
  let lastAssociatedChatsSessionId: string | null = null;
  
  // Load associated chats (sessions with the same chat_context_key)
  // sessionIdToExclude: Optional session ID to exclude from results (defaults to currentChatSessionId)
  async function loadAssociatedChats(sessionIdToExclude?: string | null) {
    if (!object_id) {
      associatedChats = [];
      return;
    }
    
    const contextKey = getCurrentChatContextKey();
    if (!contextKey) {
      associatedChats = [];
      currentChatContextKey = null;
      lastAssociatedChatsSessionId = null;
      return;
    }
    
    // Use provided sessionIdToExclude, or fall back to currentChatSessionId
    const sessionToExclude = sessionIdToExclude !== undefined ? sessionIdToExclude : currentChatSessionId;
    
    // Reload if context key changed OR if current session changed (to update exclusion)
    const contextKeyChanged = currentChatContextKey !== contextKey;
    const sessionChanged = lastAssociatedChatsSessionId !== sessionToExclude;
    
    if (!contextKeyChanged && !sessionChanged && associatedChats.length > 0) {
      return;
    }
    
    // Only show loading state if we currently have chats (replacing content)
    // If we're empty, update silently to avoid flickering "Loading..." when going from "None" to "None"
    const hasCurrentChats = associatedChats.length > 0;
    
    if (contextKeyChanged) {
      currentChatContextKey = contextKey;
    }
    if (sessionChanged) {
      lastAssociatedChatsSessionId = sessionToExclude;
    }
    
    // Only set loading state if we're replacing existing chats
    if (hasCurrentChats) {
      isLoadingAssociatedChats = true;
    }
    
    try {
      const response = await api.post("/chat2GetSessions", {
        object_id: object_id,
      });
      
      if (response.data?.sessions) {
        // Filter sessions that have the same last_context_key, excluding the current session
        const filtered = response.data.sessions.filter((session: AssociatedChatSession) => {
          return session.last_context_key === contextKey && 
                 session.chat_session_id !== sessionToExclude;
        });
        associatedChats = filtered;
     } else {
        associatedChats = [];
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error loading associated chats" });
      associatedChats = [];
    } finally {
      if (hasCurrentChats) {
        isLoadingAssociatedChats = false;
      }
    }
  }

  // Group associated chats by time period (same logic as ChatHistory)
  function groupAssociatedChatsByTimePeriod(chats: AssociatedChatSession[]): Record<TimePeriod, AssociatedChatSession[]> {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
    const lastWeek = new Date(thisWeek);
    lastWeek.setDate(thisWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(today.getMonth() - 1);

    const grouped: Record<TimePeriod, AssociatedChatSession[]> = {
      "Today": [],
      "Yesterday": [],
      "This Week": [],
      "Last Week": [],
      "Last Month": [],
      "Earlier": [],
    };

    chats.forEach((chat) => {
      // Use last_message_time if available, otherwise fall back to started
      const dateToUse = chat.last_message_time || chat.started;
      const chatDate = new Date(dateToUse);
      const chatDateOnly = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());
      
      if (chatDateOnly.getTime() === today.getTime()) {
        grouped["Today"].push(chat);
      } else if (chatDateOnly.getTime() === yesterday.getTime()) {
        grouped["Yesterday"].push(chat);
      } else if (chatDateOnly >= thisWeek) {
        grouped["This Week"].push(chat);
      } else if (chatDateOnly >= lastWeek) {
        grouped["Last Week"].push(chat);
      } else if (chatDateOnly >= lastMonth) {
        grouped["Last Month"].push(chat);
      } else {
        grouped["Earlier"].push(chat);
      }
    });

    return grouped;
  }

  // Reactive statement to group associated chats
  $: groupedAssociatedChats = groupAssociatedChatsByTimePeriod(associatedChats);
  
  // Track previous displayed content to detect actual changes
  // This prevents transitions from triggering when reloading with the same content (e.g., "None" -> "None")
  let previousAssociatedChatsContent: string = "";
  
  // Create a transition key that only changes when the actual displayed content changes
  $: {
    // Create a content signature based on the actual displayed chats (sorted by ID for consistency)
    // This signature represents what the user actually sees, not metadata
    const sortedChatIds = [...associatedChats]
      .map(c => c.chat_session_id)
      .sort()
      .join(',');
    
    // Create signature: loading state, empty state, or actual chat IDs
    const contentSignature = isLoadingAssociatedChats 
      ? `loading_${sortedChatIds}` 
      : associatedChats.length === 0 
        ? 'empty' 
        : sortedChatIds;
    
    // Only update if the content actually changed (prevents "None" -> "None" transitions)
    if (contentSignature !== previousAssociatedChatsContent) {
      previousAssociatedChatsContent = contentSignature;
    }
  }
  
  // Use the content signature as the transition key - only changes when content actually changes
  $: associatedChatsTransitionKey = previousAssociatedChatsContent;
  
  // Reactive statement to reload associated chats when current session changes
  // This ensures that when switching chats, the associated chats list updates
  $: if (currentChatSessionId && sessions.length > 0 && object_id && !isLoadingSession) {
    // Reload associated chats when switching to a different chat
    // The loadAssociatedChats function will check if reload is needed
    loadAssociatedChats();
  }
  
  // Reactive statement to reload associated chats when context changes (projects/documents selection)
  // This ensures that associated chats update immediately when the user selects different items,
  // just like Common Questions does, without needing to submit a question first
  $: if (object_id && (derivedProjects.length > 0 || derivedDocuments.length > 0)) {
    // Reload associated chats when context changes
    // The loadAssociatedChats function will check if reload is needed
    loadAssociatedChats();
  }
  
  // Reactive subscription to chatSessionStore - reload associated chats when a new question is added
  $: {
    const update = $chatSessionStore;
    if (update && update.type === "questionAdded" && update.sessionId) {
      // Reload associated chats to see if the new chat has the same context
      // Pass the sessionId from the update to ensure we exclude the correct session
      // This prevents the current chat from appearing in the associated chats list
      loadAssociatedChats(update.sessionId);
    }
  }
  
  // Format time for Today/Yesterday (e.g., "12:16am")
  function formatAssociatedChatTime(dateString: string): string {
    try {
      const date = new Date(dateString);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes}${ampm}`;
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error formatting time" });
      return "";
    }
  }
  
  // Format date for older periods (e.g., "5 Jan 2026")
  function formatAssociatedChatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString('en-GB', { month: 'short' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error formatting date" });
      return "";
    }
  }
  
  // Toggle time period expansion
  function toggleAssociatedChatsTimePeriod(period: TimePeriod) {
    associatedChatsTimePeriodExpanded[period] = !associatedChatsTimePeriodExpanded[period];
    associatedChatsTimePeriodExpanded = { ...associatedChatsTimePeriodExpanded };
  }
  
  // Get time period type
  function getAssociatedChatsTimePeriod(period: string): TimePeriod {
    return period as TimePeriod;
  }

  async function loadSessionQA(sessionId: string) {
    if (!object_id || !sessionId) {
      return;
    }
    
    // Don't overwrite messages if we're currently loading a session
    if (isLoadingSession) {
      return;
    }
    
    // If we've manually loaded a session, only allow loading Q&A for that same session
    // Check manuallyLoadedSessionIds Set (module-level, persists across remounts)
    if (manuallyLoadedSessionIds.size > 0 && !manuallyLoadedSessionIds.has(sessionId)) {
      return;
    }
    
    if ((lastLoadedSessionId || hasLoadedSession) && sessionId !== lastLoadedSessionId) {
      return;
    }
    
    // Don't overwrite if this session doesn't match the current session
    // But only if we haven't manually loaded a session (allow it if session was manually loaded)
    if (currentChatSessionId && currentChatSessionId !== sessionId && manuallyLoadedSessionIds.size === 0) {
      return;
    }
    
    try {
      const response = await api.post("/chat2GetSessionQA", {
        object_id: object_id,
        chat_session_id: sessionId,
      });
     
      if (response.data?.qa_records) {
        const qaRecords = response.data.qa_records;
                
        // Match Q&A records to messages by order (assistant messages correspond to responses)
        // We need to match across all chat contexts, not just currentChatId
        // Each Q&A record represents one question-answer pair
        // The question becomes a user message, the response becomes an assistant message
        
        // Get all chat contexts to find matching messages
        const allChatContexts = chatManager.getAllChatContexts();
        
        // Try to match Q&A records to messages in the current chat
        if (currentChatId && currentChatId !== "") {
          const messages = chatManager.getMessagesForChat(currentChatId);
           
          // Match Q&A records to assistant messages by order
          let assistantIndex = 0;
          
          for (const qa of qaRecords) {
            // Find the corresponding assistant message (responses come after questions)
            if (assistantIndex < messages.length) {
              // Find next assistant message
              while (assistantIndex < messages.length && messages[assistantIndex].role !== 'assistant') {
                assistantIndex++;
              }
              
              if (assistantIndex < messages.length && messages[assistantIndex].role === 'assistant') {
                // Use chat_qa_id from the message or Q&A record
                const qaId = messages[assistantIndex].chat_qa_id || qa.chat_qa_id;
                if (!qaId) {
                  logError(new Error(`Cannot store metadata: message at index ${assistantIndex} and Q&A record both missing chat_qa_id`), "Chatbot2", undefined);
                  assistantIndex++;
                  continue;
                }
                const sentDate = new Date(qa.sent);
                const duration = qa.response_time ? Math.round(qa.response_time) : undefined;
                // Parse route if it's a string (PostgreSQL JSONB might return as string)
                let route = qa.route || null;
                if (route && typeof route === 'string') {
                  try {
                    route = JSON.parse(route);
                  } catch (e) {
                    logError(e, "Chatbot2", undefined, { message: `Failed to parse route JSON for qaId ${qaId}` });
                  }
                }
                
                messageMetadata.set(qaId, {
                  timestamp: sentDate,
                  duration: duration,
                  route: route,
                });
                
                // Verify route was stored
                const storedMetadata = messageMetadata.get(qaId);
               
                assistantIndex++;
              }
            }
          }
        }
        
        // Save updated metadata
        saveMessageMetadata();
        
        // Update UI if this is the current chat
        // Only update if this session matches the current session ID
        if (currentChatId && currentChatId !== "" && currentChatSessionId === sessionId) {
          const updatedMessages = chatManager
            .getMessagesForChat(currentChatId)
            .map((m, index) => {
              // chat_qa_id is required - every ChatMessage should have it
              const qaId = m.chat_qa_id;
              if (!qaId) {
                logError(new Error(`ChatMessage at index ${index} missing chat_qa_id`), "Chatbot2", undefined);
                return null;
              }
              const metadata = messageMetadata.get(qaId);
              return {
                id: qaId, // Use chat_qa_id as the message ID
                chat_qa_id: qaId,
                role: m.role,
                content: m.content,
                timestamp: metadata?.timestamp,
                duration: metadata?.duration,
              };
            }).filter((msg) => msg !== null);
          setMessages(updatedMessages);
        }
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error loading session Q&A" });
    }
  }

  async function renameSession(newName: string) {
    if (!currentChatSessionId || !object_id) return;
    
    try {
      const response = await api.post("/chat2RenameSession", {
        object_id: object_id,
        chat_session_id: currentChatSessionId,
        new_name: newName,
      });
      if (response.data) {
        currentSessionName = newName;
        isEditingSessionName = false;
        await loadSessions(); // Refresh sessions list
        // Notify ChatHistory of name change
        chatSessionStore.notifyNameChanged(currentChatSessionId, newName);
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error renaming session" });
    }
  }

  async function deleteSession(sessionId: string, hardDelete: boolean = false) {
    if (!object_id) return;
    
    try {
      const response = await api.post("/chat2DeleteSession", {
        chat_session_id: sessionId,
        hardDelete: hardDelete,
      });
      if (response.success) {
        // If we deleted the current session, clear it
        if (sessionId === currentChatSessionId) {
          currentChatSessionId = null;
          setPersistedSessionId(null); // Also clear persisted value
          const newName = getNewChatName(); // Use incremented counter for new chat name
          currentSessionName = newName;
          currentSessionStarted = null;
          setMessages([]);
        }
        await loadSessions(); // Refresh sessions list
      }
    } catch (error) {
      logError(error, "Chatbot2", undefined, { message: "Error deleting session" });
    }
  }

  function autofocus(node: HTMLInputElement) {
    node.focus();
    node.select();
    return {
      destroy() {},
    };
  }

  function handleEditSessionName() {
    originalSessionName = currentSessionName; // Store original value
    isEditingSessionName = true;
  }

  function handleSaveSessionName() {
    if (currentSessionName.trim()) {
      renameSession(currentSessionName.trim());
    } else {
      const newName = getNewChatName(); // Use incremented counter for new chat name
      currentSessionName = newName;
      isEditingSessionName = false;
    }
  }

  function handleCancelEditSessionName() {
    currentSessionName = originalSessionName; // Restore original value
    isEditingSessionName = false;
    originalSessionName = "";
  }

  function formatSessionDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const today = new Date();
      
      const isToday = 
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
      
      if (isToday) {
        return "Today";
      }
      
      // Format as "23-Dec-2025" with capitalized month
      const day = date.getDate();
      const month = date.toLocaleDateString("en-GB", { month: "short" }); 
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateString;
    }
  }

  function getMessageMetadata(message: any): { timestamp?: Date; duration?: number; route?: any } {
    const lookupKey = message.chat_qa_id || message.id;
    
    // Always check the messageMetadata map first to get the most complete data (including route)
    if (lookupKey) {
      const mapMetadata = messageMetadata.get(lookupKey);
      if (mapMetadata) {
        // If message object also has timestamp/duration, merge them (message object takes precedence for those)
        let timestamp: Date | undefined = mapMetadata.timestamp;
        let duration: number | undefined = mapMetadata.duration;
        
        // Check message object for timestamp/duration (may be more up-to-date)
        if (message.timestamp || message.duration !== undefined) {
          if (message.timestamp) {
            if (message.timestamp instanceof Date) {
              timestamp = message.timestamp;
            } else if (typeof message.timestamp === 'string') {
              timestamp = new Date(message.timestamp);
            } else if (typeof message.timestamp === 'number') {
              timestamp = new Date(message.timestamp);
            }
          }
          if (message.duration !== undefined) {
            duration = message.duration;
          }
        }
        
        const result = {
          timestamp: timestamp,
          duration: duration,
          route: mapMetadata.route, // Always use route from map (not from message object)
        };
        return result;
      }
    }
    
    // Fallback: Try to get metadata from message object if map lookup failed
    if (message.timestamp || message.duration !== undefined) {
      let timestamp: Date | undefined = undefined;
      if (message.timestamp) {
        if (message.timestamp instanceof Date) {
          timestamp = message.timestamp;
        } else if (typeof message.timestamp === 'string') {
          timestamp = new Date(message.timestamp);
        } else if (typeof message.timestamp === 'number') {
          timestamp = new Date(message.timestamp);
        }
      }
      const result = {
        timestamp: timestamp,
        duration: message.duration,
        route: message.route,
      };
      return result;
    }
   
    return {};
  }

  function formatMessageTimestamp(timestamp: Date | undefined, duration: number | undefined): string {
    if (!timestamp) return "";
    
    try {
      const day = timestamp.getDate();
      const month = timestamp.toLocaleDateString("en-GB", { month: "short" });
      const year = timestamp.getFullYear();
      const dateStr = `${day}-${month}-${year}`;
      
      const timeStr = timestamp.toLocaleTimeString("en-GB", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).toLowerCase().replace(/\s/g, ""); // Remove spaces, e.g., "3:27 pm" -> "3:27pm"
      
      const durationStr = duration !== undefined ? `${duration}s` : "";
      
      return `${dateStr} ${timeStr}${durationStr ? ` (${durationStr})` : ""}`;
    } catch {
      return "";
    }
  }
</script>

<main
  style="height: {external
    ? '100vh'
    : 'calc(100vh - 9rem)'}; padding: {external && '0 1rem 1rem'}"
>
  <Splitpanes class="chat-splitpanes" horizontal={false} on:resized={handleSplitterResized}>
    <Pane size={splitPosition} minSize={30}>
      <div class="chat-mainpanel flex flex-col h-full">
        {#if currentChatSessionId}
          <div class="chat-header">
            <div class="chat-header-info">
              {#if isEditingSessionName}
                <div class="session-edit-mode">
                  <input
                    type="text"
                    class="session-edit-input"
                    bind:value={currentSessionName}
                    on:keydown={(e) => {
                      if (e.key === "Enter") handleSaveSessionName();
                      if (e.key === "Escape") handleCancelEditSessionName();
                    }}
                    use:autofocus
                  />
                  <button class="action-button" on:click={handleSaveSessionName} type="button" title="Save">
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                  <button class="action-button" on:click={handleCancelEditSessionName} type="button" title="Cancel">
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              {:else}
                <span class="title">
                  {currentSessionName}
                </span>
                <div class="chat-header-actions">
                  {#if currentSessionStarted}
                    <span class="datetime">{formatSessionDate(currentSessionStarted)}</span>
                  {/if}
                  {#if currentChatSessionId}
                    <button type="button" class="chat-header-edit" aria-label="Edit" title="Edit" on:click={handleEditSessionName}>
                      <FontAwesomeIcon icon={faPencil} />
                    </button>
                    <button type="button" class="chat-header-edit" aria-label="Delete" title="Delete" on:click={() => currentChatSessionId && deleteSession(currentChatSessionId)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  {/if}
                </div>
              {/if}
            </div>
            <div class="gap-1 flex">
              {#if $userStore.user?.user_type === "oh-user"}
                <Button type="button" class="toggle-routes-button" aria-label={showRoutes ? "Hide Routes" : "Show Routes"} title={showRoutes ? "Hide Routes" : "Show Routes"} on:click={() => { 
                  showRoutes = !showRoutes; 
                }}>
                  <FontAwesomeIcon icon={faCode} class="mr-2" />{showRoutes ? "Hide Routes" : "Show Routes"}
                </Button>
              {/if}
              <Button type="button" class="new-chat-button" aria-label="New Chat" title="New Chat" on:click={restartChat}>
                <FontAwesomeIcon icon={faPenToSquare} class="mr-2" />New Chat
              </Button>
            </div>
          </div>
        {/if}

        {#if $chatState.showEmptyState && (!$messages || $messages.length === 0) && !isLoadingSession}
          <div class="chat-empty-state-top" transition:fade={{ duration: 300 }}>
            <div class="text-center text-white">
              <FontAwesomeIcon icon={faComments} class="w-8 h-8 help-icon" />
              <p class="help-text">What can I help you with?</p>
            </div>
            <div class="chat-question chat-question-top">
              <Textarea bind:value={$input} placeholder={placeholderText} style="resize: none;" on:keydown={handleKeydown} />
              <Button type="button" class="right mb-0 rounded-full" aria-label="Submit" disabled={!$input.trim().length} on:click={handleSubmitWithLogging}>
                <FontAwesomeIcon icon={faArrowUp} />
              </Button>
            </div>
          </div>
        {/if}

        <ul bind:this={messagesContainer} class="chat-messages relative overflow-y-auto p-3 {($messages && $messages.length > 0) ? 'flex-1 flex flex-col gap-3 flex-grow' : 'hidden'}">
          {#if $messages && $messages.length > 0}
            {#each $messages as message, index}
              {@const meta = getMessageMetadata(message)}
              {@const isSeparator = message.content === "__CONTEXT_SEPARATOR__"}
              
              {#if isSeparator}
                <!-- Skip separator messages - context bar replaces them -->
              {:else}
                {@const isUserMessage = message.role === 'user'}
                {@const contextKey = isUserMessage ? getMessageContextKey(message) : null}
                {@const context = isUserMessage && contextKey ? chatContexts.get(contextKey) : null}
                
                {@const prevUserMessageIndex = (() => {
                  // Find the previous user message index
                  for (let i = index - 1; i >= 0; i--) {
                    if ($messages[i].role === 'user' && $messages[i].content !== "__CONTEXT_SEPARATOR__") {
                      return i;
                    }
                  }
                  return -1; // No previous user message
                })()}
                
                {@const prevUserMessageContextKey = prevUserMessageIndex >= 0 ? getMessageContextKey($messages[prevUserMessageIndex]) : null}
                {@const isFirstUserMessage = isUserMessage && prevUserMessageIndex === -1}
                {@const contextChanged = isUserMessage && !isFirstUserMessage && contextKey && prevUserMessageContextKey && prevUserMessageContextKey !== contextKey}
                {@const shouldShowContext = isUserMessage && context && context.items.length > 0 && (isFirstUserMessage || contextChanged)}
                
                {#if isUserMessage && shouldShowContext}
                  {@const contextKey_expanded = `context_${contextKey}_${index}`}
                  {@const isExpanded = contextExpanded.has(contextKey_expanded)}
                  {@const firstThreeItems = context.items.slice(0, 3)}
                  {@const hasMoreThanOne = context.items.length > 1}
                  {@const hasMoreThanThree = context.items.length > 3}
                  {@const isFinalContext = (() => {
                    // Check if there are any more user messages after this one with a context
                    for (let i = index + 1; i < $messages.length; i++) {
                      if ($messages[i].role === 'user' && $messages[i].content !== "__CONTEXT_SEPARATOR__") {
                        const nextContextKey = getMessageContextKey($messages[i]);
                        if (nextContextKey && chatContexts.has(nextContextKey)) {
                          return false; // There's a later context
                        }
                      }
                    }
                    return true; // This is the final context
                  })()}
                  {@const showResetButton = !isFinalContext}
                  <!-- Sticky context bar - shows when context changes, sticks until next context bar -->
                  <li class="sticky-context-bar-wrapper">
                    <div class="sticky-context-bar">
                      <div class="chat-context-bubble">
                        <div class="context-content-wrapper">
                          <div class="context-content">
                            {#if hasMoreThanOne && !isExpanded}
                              <!-- Show collapsed view with expand button -->
                              <button
                                class="context-toggle"
                                on:click={() => {
                                  contextExpanded.add(contextKey_expanded);
                                  contextExpanded = contextExpanded; 
                                }}
                              >
                                <span class="context-chevron">
                                  <FontAwesomeIcon icon={faChevronRight} />
                                </span>
                              </button>
                              <span class="context-label">Context ({context.items.length}):</span>
                              {#if hasMoreThanThree}
                                <span class="context-items">
                                  {firstThreeItems.join(', ')} ...
                                </span>
                              {:else}
                                <span class="context-items">
                                  {context.items.join(', ')}
                                </span>
                              {/if}
                            {:else if hasMoreThanOne && isExpanded}
                              <!-- Show expanded view with collapse button -->
                              <button
                                class="context-toggle"
                                on:click={() => {
                                  contextExpanded.delete(contextKey_expanded);
                                  contextExpanded = contextExpanded; 
                                }}
                              >
                                <span class="context-chevron">
                                  <FontAwesomeIcon icon={faChevronDown} />
                                </span>
                              </button>
                              <span class="context-label">Context ({context.items.length}):</span>
                            {:else}
                              <!-- Show single item inline -->
                              <span class="context-label">Context:</span>
                              <span class="context-items">
                                {context.items.join(', ')}
                              </span>
                            {/if}
                          </div>
                          {#if showResetButton}
                            <div class="context-actions">
                              <button
                                class="context-action-button"
                                on:click={async () => {
                                  if (isExpanded) {
                                    contextExpanded.delete(contextKey_expanded);
                                    contextExpanded = contextExpanded;
                                  }
                                  // Restore context to the items in this context bar
                                  await restoreContextFromItems(context.type, context.items);
                                }}
                                title="Reset"
                              >
                                <FontAwesomeIcon icon={faUndoAlt} />
                              </button>
                            </div>
                          {/if}
                        </div>
                        {#if hasMoreThanOne && isExpanded}
                          <div class="context-items-list">
                            {#each context.items as item}
                              <div class="context-item">{item}</div>
                            {/each}
                          </div>
                        {/if}
                      </div>
                    </div>
                  </li>
                {/if}
                
                {#if isUserMessage && shouldShowContext}
                  <!-- Question message - shown with context bar above -->
                  <li transition:fade={{ duration: 200 }}>
                    <div class="flex {message.role === 'user' ? 'justify-end' : 'justify-start'}">
                      <div class="flex flex-col">
                        <div
                          class="max-w-[80%] rounded-lg p-2 {message.role === 'user'
                            ? 'chat-question-bubble rounded-tr-none'
                            : 'chat-answer-bubble rounded-tl-none'}"
                        >
                          {@html markdownToHtml(getDisplayedText(message))}
                        </div>
                        {#if message.role === "user"}
                          <div class="chat-question-bubble-footer">
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                            <!-- svelte-ignore a11y-interactive-supports-focus -->
                            <span 
                              class="copy-btn" 
                              role="button" 
                              title="Copy Question" 
                              style="position: relative; display: inline-block;"
                              on:click={createQuestionCopyHandler(index)}
                            >
                              <FontAwesomeIcon icon={faCopy} />
                              {#if showQuestionCopyToast === index}
                                <div class="copy-toast-wrapper">
                                  <Toast color="blue" dismissable={false} transition={fly} params={{ x: 0, duration: 2000 }} class="copy-toast-blue">
                                    Copied!
                                  </Toast>
                                </div>
                              {/if}
                            </span>
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                            <!-- svelte-ignore a11y-interactive-supports-focus -->
                            <span 
                              class="copy-btn" 
                              role="button" 
                              title="Resubmit Question" 
                              style="position: relative; display: inline-block;"
                              on:click={() => resubmitQuestion(index)}
                            >
                              <FontAwesomeIcon icon={faRotateRight} />
                            </span>
                          </div>
                        {/if}
                      </div>
                    </div>
                  </li>
                {:else}
                  <!-- Non-user messages or user messages without context bar -->
                  <li
                    class="flex {message.role === 'user'
                      ? 'justify-end'
                      : 'justify-start'}"
                  >
                    <div class="flex flex-col">
                      <div
                        class="max-w-[80%] rounded-lg p-2 {message.role === 'user'
                          ? 'chat-question-bubble rounded-tr-none'
                          : 'chat-answer-bubble rounded-tl-none'}"
                      >
                        {@html markdownToHtml(getDisplayedText(message))}
                      </div>
                      {#if message.role === "user"}
                        <div class="chat-question-bubble-footer">
                          <!-- svelte-ignore a11y-click-events-have-key-events -->
                          <!-- svelte-ignore a11y-no-static-element-interactions -->
                          <!-- svelte-ignore a11y-interactive-supports-focus -->
                          <span 
                            class="copy-btn" 
                            role="button" 
                            title="Copy Question" 
                            style="position: relative; display: inline-block;"
                            on:click={createQuestionCopyHandler(index)}
                          >
                            <FontAwesomeIcon icon={faCopy} />
                            {#if showQuestionCopyToast === index}
                              <div class="copy-toast-wrapper">
                                <Toast color="blue" dismissable={false} transition={fly} params={{ x: 0, duration: 2000 }} class="copy-toast-blue">
                                  Copied!
                                </Toast>
                              </div>
                            {/if}
                          </span>
                          <!-- svelte-ignore a11y-click-events-have-key-events -->
                          <!-- svelte-ignore a11y-no-static-element-interactions -->
                          <!-- svelte-ignore a11y-interactive-supports-focus -->
                          <span 
                            class="copy-btn" 
                            role="button" 
                            title="Resubmit Question" 
                            style="position: relative; display: inline-block;"
                            on:click={() => resubmitQuestion(index)}
                          >
                            <FontAwesomeIcon icon={faRotateRight} />
                          </span>
                        </div>
                      {:else if message.role === "assistant"}
                        {@const nextStepValue = meta.route?.next_step}
                        {@const hasNextStep = nextStepValue && nextStepValue !== NEXT_STEP_NONE}
                        {@const routeChoice = meta.route?.next_step_choice}
                        {@const memoryChoice = nextStepChoices.get(message.chat_qa_id || message.id)}
                        {@const choice = routeChoice || memoryChoice}
                        {#if hasNextStep}
                          <div class="next-step-bubble mt-2">
                            <div class="next-step-content">
                              {#if choice}
                                <!-- Show choice result -->
                                <div class="next-step-message">
                                  <strong>Next Steps:</strong> {getNextStepChoiceText(choice)}
                                </div>
                              {:else}
                                <!-- Show question and buttons -->
                                <div class="next-step-message">
                                  <strong>Next Steps:</strong> {getNextStepMessage(nextStepValue)}
                                </div>
                                {@const options = getNextStepOptions(nextStepValue)}
                                {#if options && options.length > 0}
                                  <div class="next-step-buttons">
                                    {#each options as option}
                                      <Button
                                        type="button"
                                        class="next-step-button {option.isResubmit ? 'next-step-button-resubmit' : 'next-step-button-do-nothing'}"
                                        on:click={() => handleNextStepChoice(message.chat_qa_id || message.id, nextStepValue, option.value)}
                                      >
                                        {option.label}
                                      </Button>
                                    {/each}
                                  </div>
                                {/if}
                              {/if}
                            </div>
                          </div>
                        {/if}
                        <div class="chat-answer-bubble-footer">
                          <!-- svelte-ignore a11y-click-events-have-key-events -->
                          <!-- svelte-ignore a11y-no-static-element-interactions -->
                          <!-- svelte-ignore a11y-interactive-supports-focus -->
                          <span 
                            class="copy-btn" 
                            role="button" 
                            title="Copy Question and Answer" 
                            style="position: relative; display: inline-block;"
                            on:click={createCopyHandler(index)}
                          >
                            <FontAwesomeIcon icon={faCopy} />
                            {#if showCopyToast === index}
                              <div class="copy-toast-wrapper">
                                <Toast color="blue" dismissable={false} transition={fly} params={{ x: 0, duration: 2000 }} class="copy-toast-blue">
                                  Copied!
                                </Toast>
                              </div>
                            {/if}
                          </span>
                          {#if meta.timestamp}
                            <span class="timestamp">
                              {formatMessageTimestamp(meta.timestamp, meta.duration)}
                            </span>
                          {:else}
                            <!-- Debug: show why timestamp not showing -->
                            <span style="font-size: 0.6rem; opacity: 0.5;">
                              (no timestamp - meta: {JSON.stringify({hasTimestamp: !!meta.timestamp, hasDuration: meta.duration !== undefined, chat_qa_id: message.chat_qa_id || message.id})})
                            </span>
                          {/if}
                        </div>
                        {#if showRoutes}
                          {#if meta.route}
                            <div class="chat-route-bubble mt-2">
                              <pre class="route-json">{JSON.stringify(meta.route, null, 2)}</pre>
                            </div>
                          {:else}
                            <!-- Debug: show why route not displaying -->
                            <div class="chat-route-bubble mt-2">
                              <strong>No route data available for this message.</strong><br/>
                              showRoutes: {showRoutes}<br/>
                              Meta keys: {Object.keys(meta).join(', ')}<br/>
                              chat_qa_id: {message.chat_qa_id || message.id}<br/>
                              meta.route: {meta.route ? JSON.stringify(meta.route) : 'undefined/null'}<br/>
                              meta.route type: {typeof meta.route}
                            </div>
                          {/if}
                        {/if}
                      {/if}
                    </div>
                  </li>
                {/if}
              {/if}
            {/each}
          {/if}

          {#if $chatState.showThinkingState}
            <li
              class="flex sticky bottom-0 m-0"
              transition:fade={{ duration: 100 }}
            >
              <div class="text-xs">
                <span>
                  {#each getRippleText(revealedText) as { char, style }, i}
                    <span class="chat-ripple-text" {style}
                      >{char === " " ? "\u00A0" : char}</span
                    >
                  {/each}
                </span>
              </div>
            </li>
          {/if}
        </ul>

        {#if $messages && $messages.length > 0}
          <div class="chat-question chat-question-bottom" transition:fade={{ duration: 300 }}>
            <Textarea bind:value={$input} placeholder={placeholderText} style="resize: none;" on:keydown={handleKeydown} />
            <Button type="button" class="right mb-0 rounded-full" aria-label="Submit" disabled={!$input.trim().length} on:click={handleSubmitWithLogging}>
              <FontAwesomeIcon icon={faArrowUp} />
            </Button>
          </div>
        {/if}
      </div>
    </Pane>

    <Pane size={100 - splitPosition} minSize={20}>
      <div class="chat-sidepanel">
        <Splitpanes class="chat-sidepanel-splitpanes" horizontal={true}>
          <Pane size={50} minSize={20}>
            <div class="chat-sidepanel-section">
              <div class="chat-subheader">Common Questions</div>
              <div class="chat-sidepanel-content">
                {#if currentQuestions.length > 0}
                  <ul class="common-questions-list">
                    {#each currentQuestions as question}
                      <!-- svelte-ignore a11y-invalid-attribute -->
                      <li>
                        <a href="javascript:void(0)" on:click={() => { $input = question.question; handleSubmitWithLogging(new Event("click"), true); }}
                          class="common-question-link"
                          title={question.description}
                        >
                          {question.question}
                        </a>
                      </li>
                    {/each}
                  </ul>
                {:else}
                  <div class="chat-history-empty">
                    <p>None</p>
                  </div>
                {/if}
              </div>
            </div>
          </Pane>

          <Pane size={50} minSize={20}>
            <div class="chat-sidepanel-section">
              <div class="chat-subheader">Associated Chats</div>
              <div class="chat-history">
                {#if isLoadingAssociatedChats}
                  <div class="chat-history-empty">
                    <p>Loading...</p>
                  </div>
                {:else if associatedChats.length === 0}
                  <div class="chat-history-empty">
                    <p>None</p>
                  </div>
                {:else}
                  {#key associatedChatsTransitionKey}
                    {#each Object.entries(groupedAssociatedChats) as [period, periodChats], periodIndex}
                      {#if periodChats.length > 0}
                        <div class="time-period-group" transition:fly={{ y: -10, duration: 300, delay: periodIndex * 50 }}>
                          <button
                            class="time-period-header"
                            on:click={() => toggleAssociatedChatsTimePeriod(getAssociatedChatsTimePeriod(period))}
                            type="button"
                          >
                            <FontAwesomeIcon
                              icon={associatedChatsTimePeriodExpanded[getAssociatedChatsTimePeriod(period)] ? faChevronDown : faChevronRight}
                              class="time-period-chevron"
                            />
                            <span class="time-period-label">{period}</span>
                          </button>
                          {#if associatedChatsTimePeriodExpanded[getAssociatedChatsTimePeriod(period)]}
                            <div class="sessions-list">
                              {#each periodChats as chat, index (chat.chat_session_id)}
                                <div class="session-item" transition:fly={{ y: -10, duration: 250, delay: (periodIndex * 50) + (index * 30) }}>
                                  <div class="session-header">
                                    <button class="session-name-button" type="button"
                                      on:click={() => {
                                        // Load the associated chat session
                                        if (chat.chat_session_id) {
                                          loadSessionIntoChat(chat.chat_session_id);
                                        }
                                      }}
                                    >
                                      <span class="text">{chat.name || "Unnamed Chat"}</span>
                                      <span class="datetime">
                                        {#if period === "Today" || period === "Yesterday"}
                                          {formatAssociatedChatTime(chat.last_message_time || chat.started)}
                                        {:else}
                                          {formatAssociatedChatDate(chat.last_message_time || chat.started)}
                                        {/if}
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              {/each}
                            </div>
                          {/if}
                        </div>
                      {/if}
                    {/each}
                  {/key}
                {/if}
              </div>
            </div>
          </Pane>

        </Splitpanes>
      </div>
    </Pane>

  </Splitpanes>

</main>
